/**
 * Embodied carbon utilities: HOT2000 layer lookup tables, framing maps, and grade logic for the enclosure UI.
 *
 * Rough layout (data constants are large; search for these banners):
 * - Grade scales & colors
 * - Layer / insulation / finish **data maps** (kgCO₂e/m² unless noted), plus **whole-window** map (`glazing` × `frame`)
 * - **Material-layer** benchmarks (interior, exterior, sheathing) + ratio-based letter grades
 * - **Interior/exterior** `alt` resolution (HOT2000 groupings vs embodied sub-types)
 * - **Framing** maps, wood averages, cavity fractions, framing carbon lookup
 * - **Grading** (EPD vs map, framing wood/dynamic, windows, fallbacks) → `emissionGradeFromEmbC`, layer/framing display
 * - **Aggregation** `getEmbodiedCarbon`, option tags `withEmissionGrades`, window `overall` snapshot
 * - **Whole-home** `getOverallEmbodiedEmissions` (bottom of file; embodied totals only — no display / operational carbon)
 *
 * Letter grades on `GRADE_SCALE`: numeric value 0 = best (A+), 0.9 = worst (D).
 */

import { isEmpty } from "lodash";
import {
    getUdefEmbodiedCarbon,
    getUdefEmbodiedCarbonLayerBreakdown,
    getUdefStandardCodeLayersArray,
} from "./embc/udefStandardLayers";
import {
    getUdefWindowCodeEmbodiedCarbonDisplay,
    getUdefWindowLayerFromCode,
    WINDOW_UDEF_EMB_KEY,
    getUdefWindowLayerDisplayLabel,
} from "./embc/udefWindowLayers";

export const GRADE_SCALE = [
    { value: 0, label: "A+" },
    { value: 0.1, label: "A" },
    { value: 0.2, label: "A-" },
    { value: 0.3, label: "B+" },
    { value: 0.4, label: "B" },
    { value: 0.5, label: "B-" },
    { value: 0.6, label: "C+" },
    { value: 0.7, label: "C" },
    { value: 0.8, label: "C-" },
    { value: 0.9, label: "D" },
];

export const STORAGE_GRADE_SCALE = [
    { value: 0, label: "D" },
    { value: 0.1, label: "C-" },
    { value: 0.2, label: "C" },
    { value: 0.3, label: "C+" },
    { value: 0.4, label: "B-" },
    { value: 0.5, label: "B" },
    { value: 0.6, label: "B+" },
    { value: 0.7, label: "A-" },
    { value: 0.8, label: "A" },
    { value: 0.9, label: "A+" },
];

export const GRADE_COLORS = {
    "A+": "#18C1AD",
    A: "#5DBDA0",
    "A-": "#7EB791",
    "B+": "#96B182",
    B: "#ABAA73",
    "B-": "#BCA364",
    "C+": "#CC9B54",
    C: "#DA9344",
    "C-": "#E88933",
    D: "#F58020",
    "N/A": "#E1EAF0",
};

// -----------------------------------------------------------------------------
// Data: HOT2000 layer & component lookup tables (emissions/storage per m² unless perUnit says m²/mm)
// -----------------------------------------------------------------------------

// Insulation Layer 1 - keyed by HOT2000 code
// kgCO2e emissions and storage are per m2, except codes 9/A/E/F/U/Q/R/S/T which are per m2/mm cavity depth
export const INSULATION_LAYER_1 = {
    0: { emissions: 0.0, storage: 0.0, emissionGrade: "N/A", storageGrade: "N/A", perUnit: "m2" },
    1: { emissions: 1.3205, storage: 0.0, emissionGrade: "B", storageGrade: "N/A", perUnit: "m2" },
    K: { emissions: 1.6507, storage: 0.0, emissionGrade: "B", storageGrade: "N/A", perUnit: "m2" },
    2: { emissions: 1.9808, storage: 0.0, emissionGrade: "B", storageGrade: "N/A", perUnit: "m2" },
    X: { emissions: 2.3109, storage: 0.0, emissionGrade: "B", storageGrade: "N/A", perUnit: "m2" },
    Y: { emissions: 3.1363, storage: 0.0, emissionGrade: "B", storageGrade: "N/A", perUnit: "m2" },
    3: { emissions: 3.3013, storage: 0.0, emissionGrade: "B", storageGrade: "N/A", perUnit: "m2" },
    4: { emissions: 3.6315, storage: 0.0, emissionGrade: "B", storageGrade: "N/A", perUnit: "m2" },
    Z: { emissions: 3.9616, storage: 0.0, emissionGrade: "B", storageGrade: "N/A", perUnit: "m2" },
    a: { emissions: 4.6219, storage: 0.0, emissionGrade: "B", storageGrade: "N/A", perUnit: "m2" },
    5: { emissions: 4.6219, storage: 0.0, emissionGrade: "B", storageGrade: "N/A", perUnit: "m2" },
    L: { emissions: 5.1171, storage: 0.0, emissionGrade: "B", storageGrade: "N/A", perUnit: "m2" },
    c: { emissions: 5.7773, storage: 0.0, emissionGrade: "B", storageGrade: "N/A", perUnit: "m2" },
    J: { emissions: 6.6027, storage: 0.0, emissionGrade: "B", storageGrade: "N/A", perUnit: "m2" },
    b: { emissions: 6.6027, storage: 0.0, emissionGrade: "B", storageGrade: "N/A", perUnit: "m2" },
    d: { emissions: 6.2909, storage: 27.4592, emissionGrade: "A", storageGrade: "A+", perUnit: "m2" },
    // Per m2/mm cavity depth codes
    9: { emissions: 0.014, storage: 0.0613, emissionGrade: "A", storageGrade: "A+", perUnit: "m2/mm" },
    A: { emissions: 0.0149, storage: 0.0649, emissionGrade: "A", storageGrade: "A+", perUnit: "m2/mm" },
    E: { emissions: 0.0354, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A", perUnit: "m2/mm" },
    F: { emissions: 0.0354, storage: 0.0, emissionGrade: "C", storageGrade: "N/A", perUnit: "m2/mm" },
    U: { emissions: 0.1222, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A", perUnit: "m2/mm" },
    V: { emissions: 2.7296, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A", perUnit: "m2" },
    W: { emissions: 5.4593, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A", perUnit: "m2" },
    G: { emissions: 3.1511, storage: 0.0, emissionGrade: "C", storageGrade: "N/A", perUnit: "m2" },
    H: { emissions: 4.9017, storage: 0.0, emissionGrade: "C", storageGrade: "N/A", perUnit: "m2" },
    e: { emissions: null, storage: null, emissionGrade: "N/A", storageGrade: "N/A", perUnit: "m2" },
    M: { emissions: null, storage: null, emissionGrade: "N/A", storageGrade: "N/A", perUnit: "m2" },
    N: { emissions: null, storage: null, emissionGrade: "N/A", storageGrade: "N/A", perUnit: "m2" },
    O: { emissions: null, storage: null, emissionGrade: "N/A", storageGrade: "N/A", perUnit: "m2" },
    P: { emissions: null, storage: null, emissionGrade: "N/A", storageGrade: "N/A", perUnit: "m2" },
    Q: { emissions: 0.0131, storage: 0.1172, emissionGrade: "C", storageGrade: "A+", perUnit: "m2/mm" },
    R: { emissions: 0.0649, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A", perUnit: "m2/mm" },
    S: { emissions: 0.0952, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A", perUnit: "m2/mm" },
    T: { emissions: 0.1701, storage: 0.0, emissionGrade: "D", storageGrade: "N/A", perUnit: "m2/mm" },
};

// Cavity depths in mm for insulation layer 1 values that require mm multiplier
// Takes [structureTypeValue][componentTypeSizeValue] as key and returns depth in mm
export const CAVITY_DEPTHS = {
    2: { 0: 89, 5: 102, 1: 140, 2: 184, 3: 235, 4: 286 },
    3: { 0: 92, 1: 152 },
    4: {
        2: 451,
        3: 356,
        4: 324,
        5: 375,
        6: 451,
        7: 324,
        8: 375,
        9: 451,
        A: 324,
        B: 375,
        C: 451,
    },
    5: { 0: 241, 1: 302, 2: 356, 3: 406 },
    6: { 0: 0, 1: 64, 2: 89 },
};

// Insulation Layer 2 - keyed by HOT2000 code
export const INSULATION_LAYER_2 = {
    0: { emissions: 0.0, storage: 0.0, emissionGrade: "N/A", storageGrade: "N/A" },
    K: { emissions: 0.8243, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    L: { emissions: 1.2432, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    M: { emissions: 1.6531, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    N: { emissions: 2.4774, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    1: { emissions: 3.3017, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    O: { emissions: 4.9905, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    P: { emissions: 1.2084, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    C: { emissions: 2.4168, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    2: { emissions: 3.6253, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    A: { emissions: 4.8337, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    3: { emissions: 7.2505, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    H: { emissions: 2.1598, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    4: { emissions: 3.231, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    B: { emissions: 4.3196, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    5: { emissions: 6.488, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    I: { emissions: 8.6478, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    6: { emissions: 10.7989, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    J: { emissions: 12.9587, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    7: { emissions: 3.8017, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    Q: { emissions: 7.6034, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    R: { emissions: 1.8147, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    8: { emissions: 2.7188, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    S: { emissions: 3.6361, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    T: { emissions: 5.4508, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    U: { emissions: 7.2656, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    9: { emissions: null, storage: null, emissionGrade: "N/A", storageGrade: "N/A" }, // Same as Layer 1
    D: { emissions: 2.7296, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    E: { emissions: 5.4593, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    F: { emissions: 8.089, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    G: { emissions: 9.5564, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
};

export const BASEMENT_WALL_EXTRA_INSULATION_LAYER = {
    0: { emissions: 0.0, storage: 0.0, emissionGrade: "N/A", storageGrade: "N/A" },
    1: { emissions: 1.3205, storage: 0.0, emissionGrade: "B", storageGrade: "N/A" },
    2: { emissions: 1.9808, storage: 0.0, emissionGrade: "B", storageGrade: "N/A" },
    3: { emissions: 3.3013, storage: 0.0, emissionGrade: "B", storageGrade: "N/A" },
    4: { emissions: 3.6315, storage: 0.0, emissionGrade: "B", storageGrade: "N/A" },
    5: { emissions: 4.6219, storage: 0.0, emissionGrade: "B", storageGrade: "N/A" },
    P: { emissions: 0.8243, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    Q: { emissions: 1.2432, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    R: { emissions: 1.6531, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    S: { emissions: 2.4774, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    6: { emissions: 3.3017, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    T: { emissions: 4.9905, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    U: { emissions: 1.2084, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    H: { emissions: 2.4168, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    7: { emissions: 3.6253, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    F: { emissions: 4.8337, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    8: { emissions: 7.2505, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    M: { emissions: 2.1598, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    9: { emissions: 3.231, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    G: { emissions: 4.3196, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    A: { emissions: 6.488, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    N: { emissions: 8.6478, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    B: { emissions: 10.7989, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    O: { emissions: 12.9587, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    C: { emissions: 3.8017, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    E: { emissions: 7.6034, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    I: { emissions: 2.7296, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    J: { emissions: 5.4593, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    K: { emissions: 8.089, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    L: { emissions: 9.5564, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    V: { emissions: 1.8147, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    D: { emissions: 2.7188, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    W: { emissions: 3.6361, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    X: { emissions: 5.4508, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
    Y: { emissions: 7.2656, storage: 0.0, emissionGrade: "C-", storageGrade: "N/A" },
};

// Interior layer - keyed by HOT2000 code
export const INTERIOR = {
    0: { emissions: 0.0, storage: 0.0, emissionGrade: "N/A", storageGrade: "N/A" },
    1: { emissions: 4.7309, storage: 0.3814, emissionGrade: "B", storageGrade: "C-" },
    2: { emissions: 4.9176, storage: 7.4608, emissionGrade: "B", storageGrade: "B" },
    3: { emissions: 6.3343, storage: 21.1174, emissionGrade: "B-", storageGrade: "A" },
    4: {
        emissions: 23.85,
        storage: 0.0,
        emissionGrade: "D",
        storageGrade: "N/A",
        defaultLabel: "Tile",
        alt: {
            Linoleum: { emissions: 4.06, storage: 4.7097, emissionGrade: "B", storageGrade: "B" },
        },
    },
    5: {
        emissions: 28.5809,
        storage: 0.3814,
        emissionGrade: "D",
        storageGrade: "C-",
        defaultLabel: "Tile",
        alt: {
            Linoleum: { emissions: 8.7909, storage: 5.0911, emissionGrade: "B-", storageGrade: "B" },
        },
    },
    6: {
        emissions: 0.8141,
        storage: 30.861,
        emissionGrade: "A",
        storageGrade: "A",
        defaultLabel: "Canadian Avg.",
        alt: {
            BC: { emissions: 0.7906, storage: 27.0891, emissionGrade: "A", storageGrade: "A" },
            AB: { emissions: 1.0859, storage: 28.8036, emissionGrade: "A", storageGrade: "A" },
            ON: { emissions: 1.0401, storage: 33.9471, emissionGrade: "A", storageGrade: "A" },
            QC: { emissions: 0.4724, storage: 31.5468, emissionGrade: "A", storageGrade: "A" },
            NB: { emissions: 0.7887, storage: 33.2613, emissionGrade: "A", storageGrade: "A" },
            "Atlantic Avg.": { emissions: 0.7068, storage: 30.5181, emissionGrade: "A", storageGrade: "A" },
        },
    },
    7: { emissions: 5.545, storage: 31.2424, emissionGrade: "B", storageGrade: "A" },
    8: { emissions: 13.3, storage: 0.0, emissionGrade: "C", storageGrade: "N/A" },
    9: { emissions: 0.88, storage: 0.0, emissionGrade: "A", storageGrade: "N/A" },
};

// Sheathing layer - keyed by HOT2000 code
export const SHEATHING = {
    0: { emissions: 0.0, storage: 0.0, emissionGrade: "N/A", storageGrade: "N/A" },
    1: { emissions: 1.805, storage: 14.3161, emissionGrade: "A", storageGrade: "A" },
    2: { emissions: 2.1058, storage: 16.7021, emissionGrade: "B", storageGrade: "A" },
    3: { emissions: 3.0083, storage: 23.8601, emissionGrade: "C", storageGrade: "A" },
    4: { emissions: 2.1527, storage: 16.8878, emissionGrade: "B", storageGrade: "A" },
    5: { emissions: 2.8702, storage: 22.5171, emissionGrade: "B", storageGrade: "A" },
    6: { emissions: 3.5878, storage: 28.1464, emissionGrade: "C", storageGrade: "A" },
    7: { emissions: 4.3053, storage: 33.7757, emissionGrade: "C", storageGrade: "A" },
    8: { emissions: 1.5324, storage: 1.6885, emissionGrade: "A", storageGrade: "D" },
    9: { emissions: 1.7907, storage: 1.973, emissionGrade: "A", storageGrade: "D" },
    A: { emissions: 3.5482, storage: 0.2861, emissionGrade: "C", storageGrade: "D" },
    B: { emissions: 4.7309, storage: 0.3814, emissionGrade: "C", storageGrade: "D" },
    C: { emissions: 13.9024, storage: 0.0, emissionGrade: "D", storageGrade: "D" },
};

// Exterior layer - keyed by HOT2000 code
//EDGE CASE: Exterior field for "floorsAbove" uses INTERIOR instead of EXTERIOR
export const EXTERIOR = {
    0: { emissions: 0.0, storage: 0.0, emissionGrade: "N/A", storageGrade: "N/A" },
    1: { emissions: 0.8141, storage: 30.861, emissionGrade: "A", storageGrade: "A" },
    2: {
        emissions: 4.71,
        storage: 0.0,
        emissionGrade: "C",
        storageGrade: "N/A",
        defaultLabel: "Vinyl",
        alt: { Metal: { emissions: 13.9241, storage: 0, emissionGrade: "C-", storageGrade: "N/A" } },
    },
    3: {
        emissions: 5.2909,
        storage: 0.0,
        emissionGrade: "C",
        storageGrade: "N/A",
        defaultLabel: "Vinyl",
        alt: { Metal: { emissions: 14.505, storage: 0, emissionGrade: "C-", storageGrade: "N/A" } },
    },
    4: { emissions: 47.2484, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    5: { emissions: 2.18, storage: 0.0, emissionGrade: "B", storageGrade: "N/A" },
    6: { emissions: 35.0, storage: 0.0, emissionGrade: "D", storageGrade: "N/A" },
    7: { emissions: null, storage: null, emissionGrade: "N/A", storageGrade: "N/A" },
    8: { emissions: null, storage: null, emissionGrade: "N/A", storageGrade: "N/A" },
    9: { emissions: null, storage: null, emissionGrade: "N/A", storageGrade: "N/A" },
};

export const FOUNDATION_EMISSIONS = {
    concrete: { emissions: 273.67, storage: 0, source: "Canadian average (25MPa or less)" }, //kgCO2/m3
    aggregate: { emissions: 16.01264, storage: 0, source: "Average construction aggregate" }, //kgCO2/m3
    rebar: { emissions: 0.677455, storage: 0, source: "Rebar average (10M)" }, //kgCO2/m
    steelMesh: { emissions: 3.5465, storage: 0, source: "Steel mesh average (6x6)" }, //kgCO2/m2
};

export const FOUNDATION_SLAB_INSULATION_EMISSIONS = {
    // User Specified looks like a codeReference
    0: { emissions: 3.3017, storage: 0, source: "50 mm EPS I (2 in)" }, // 50 mm EPS I (2 in)
    1: { emissions: 3.6253, storage: 0, source: "38 mm EPS II (1.5 in)" }, // 38 mm EPS II (1.5 in)
    2: { emissions: 4.8337, storage: 0, source: "50 mm EPS II (2 in)" }, // 50 mm EPS II (2 in)
    3: { emissions: 7.2505, storage: 0, source: "76 mm EPS II (3 in)" }, // 76 mm EPS II (3 in)
    4: { emissions: 3.231, storage: 0, source: "19 mm XTPS IV (0.75 in)" }, // 19 mm XTPS IV (0.75 in)
    5: { emissions: 6.488, storage: 0, source: "38 mm XTPS IV (1.5 in)" }, // 38 mm XTPS IV (1.5 in)
    6: { emissions: 10.7989, storage: 0, source: "64 mm XTPS IV (2.5 in)" }, // 64 mm XTPS IV (2.5 in)
    7: { emissions: 3.8017, storage: 0, source: "25 mm semi-rigid (1 in)" }, // 25 mm semi-rigid (1 in)
    8: { emissions: 2.7188, storage: 0, source: "19 mm polyisocyan.(0.75 in)" }, // 19 mm polyisocyan.(0.75 in)
    9: { emissions: 7.6034, storage: 0, source: "50 mm rig. glass fib.(2 in)" }, // 50 mm rig. glass fib.(2 in)
};

// -----------------------------------------------------------------------------
// Whole-window defaults (kgCO₂e/m² emissions & storage) — glazing category × frame HOT2000 code
// -----------------------------------------------------------------------------
// Same shape as layer maps above: numeric string keys from client fields `glazingType` / `frameMaterial`.
// Glazing buckets match prior branching: “double” = 2,5,6,7,8; “triple” = 3,4. Other glazing → no map row.

//TODO: Need to handle overall window characteristic windows otherwise we'll always be showing unaccounted for windows.
/** csWindowGlazingType values that use the `double` column in {@link STANDARD_WINDOW_EMBODIED_BY_GLAZING_AND_FRAME}. */
export const WINDOW_DOUBLE_GLAZING_TYPES = ["2", "5", "6", "7", "8"];

/** csWindowGlazingType values that use the `triple` column. */
export const WINDOW_TRIPLE_GLAZING_TYPES = ["3", "4"];

const WINDOW_DOUBLE_GLAZING_SET = new Set(WINDOW_DOUBLE_GLAZING_TYPES);
const WINDOW_TRIPLE_GLAZING_SET = new Set(WINDOW_TRIPLE_GLAZING_TYPES);

/**
 * Nested like INSULATION_LAYER_1: first key = glazing bucket (`double` | `triple`), second = frame material code (string).
 * Comments in UI: 0/1 aluminum, 2 wood, 3 aluminum-clad wood, 4/5 vinyl, 6 fibreglass.
 */
export const STANDARD_WINDOW_EMBODIED_BY_GLAZING_AND_FRAME = {
    double: {
        0: { emissions: 99.0, storage: 0 },
        1: { emissions: 99.0, storage: 0 },
        2: { emissions: 72.0, storage: 16.4286 },
        3: { emissions: 99.0, storage: 8.1405 },
        4: { emissions: 86.0, storage: 0.0 },
        5: { emissions: 86.0, storage: 0.0 },
        6: { emissions: 114.0, storage: 0.0 },
    },
    triple: {
        0: { emissions: 112.0, storage: 0.0 },
        1: { emissions: 112.0, storage: 0.0 },
        2: { emissions: 85.0, storage: 16.4286 },
        3: { emissions: 112.0, storage: 8.1405 },
        4: { emissions: 99.0, storage: 0.0 },
        5: { emissions: 99.0, storage: 0.0 },
        6: { emissions: 127.0, storage: 0.0 },
    },
};

/** @returns {{ emissions: number, storage: number } | null} */
export function lookupStandardWindowCodeEmbodiedDefaults(glazingTypeValue, frameMaterialValue) {
    const g = (glazingTypeValue ?? "1").toString();
    const f = (frameMaterialValue ?? "0").toString();
    let glazingCategory;
    if (WINDOW_DOUBLE_GLAZING_SET.has(g)) glazingCategory = "double";
    else if (WINDOW_TRIPLE_GLAZING_SET.has(g)) glazingCategory = "triple";
    else return null;
    return STANDARD_WINDOW_EMBODIED_BY_GLAZING_AND_FRAME[glazingCategory]?.[f] ?? null;
}

// -----------------------------------------------------------------------------
// Material layers (interior / exterior / sheathing): benchmark-based emission grades
// -----------------------------------------------------------------------------
// Uses one industry benchmark kgCO₂e/m² per category (see MATERIAL_EMISSIONS_BENCHMARKS). Letter grade is
// driven by ratio r = actual emissions / benchmark. Anchors: 0.5×→A+, 0.7×→A, 1.0×→B, 2×→C, 4×→D, with
// intermediate steps for A-…C-. Insulation layers 1 & 2 do NOT use this system (they use map grades / RSI logic elsewhere).

/** Industry benchmark kgCO₂e/m² per category; r = 1 is the notional “B” reference point. */
export const MATERIAL_EMISSIONS_BENCHMARKS = {
    interior: { emissions: 4.74 }, // Drywall
    exterior: { emissions: 4.71 }, // Vinyl
    sheathing: { emissions: 3.0 }, // OSB
};

/** [maxRatio, grade] pairs; first match where r <= maxRatio wins (r = emissions / benchmark). */
const MATERIAL_EMISSION_RATIO_TIERS = [
    [0.5, "A+"],
    [0.7, "A"],
    [0.82, "A-"],
    [0.91, "B+"],
    [1.0, "B"],
    [1.35, "B-"],
    [1.7, "C+"],
    [2.0, "C"],
    [3.0, "C-"],
    [4.0, "D"],
];

export function getMaterialEmissionsBenchmarkValue(layerType) {
    const keyByLayer = {
        interior: "interior",
        interiorFinish: "interior",
        exterior: "exterior",
        sheathing: "sheathing",
    };
    const key = keyByLayer[layerType];
    if (key == null) return null;
    const entry = MATERIAL_EMISSIONS_BENCHMARKS[key];
    if (entry == null) return null;
    const v = typeof entry === "number" ? entry : entry.emissions;
    return v != null && Number.isFinite(v) && v > 0 ? v : null;
}

export function getMaterialLayerEmissionGrade(emissions, benchmark) {
    if (emissions == null || !Number.isFinite(emissions) || benchmark == null || !(benchmark > 0)) {
        return "N/A";
    }
    /** Zero kg/m² is “no embodied emissions” — no letter grade / bar fill (same as insulation “None”). */
    if (emissions === 0) return "N/A";
    const r = emissions / benchmark;
    for (const [maxR, grade] of MATERIAL_EMISSION_RATIO_TIERS) {
        if (r <= maxR) return grade;
    }
    return "D";
}

// -----------------------------------------------------------------------------
// Interior / exterior: HOT2000 row resolution (grouped energy codes vs embodied `alt` rows)
// -----------------------------------------------------------------------------

/** Stored on `layers` as `${layerSelectKey}_materialAltKey` (e.g. `interior_materialAltKey`). */
export const getLayerMaterialAltFieldKey = (layerSelectKey) => `${layerSelectKey}_materialAltKey`;

/**
 * INTERIOR / EXTERIOR rows may include `defaultLabel` + `alt` (label → { emissions, storage, grades }).
 * When `materialAltKey` matches an `alt` entry, use that variant; otherwise use the grouped HOT2000 default (top-level fields).
 */
export function resolveInteriorExteriorMaterialRow(mapRow, materialAltKey) {
    if (!mapRow) return {};
    if (!mapRow.alt) {
        const { alt: _a, defaultLabel: _d, ...rest } = mapRow;
        return rest;
    }
    const key = materialAltKey != null && materialAltKey !== "" ? String(materialAltKey) : "";
    if (key && mapRow.alt[key]) {
        const v = mapRow.alt[key];
        return {
            emissions: v.emissions,
            storage: v.storage,
            emissionGrade: v.emissionGrade,
            storageGrade: v.storageGrade,
            source: v.source,
            perUnit: mapRow.perUnit,
        };
    }
    const { alt: _a, defaultLabel: _d, ...rest } = mapRow;
    return rest;
}

// -----------------------------------------------------------------------------
// Framing (structure): maps, wood averages, cavity fractions
// -----------------------------------------------------------------------------
// Framing (Structure) data - keyed by structureType value + spacing code + componentTypeSize code
// Wood Frame structure type value = "2"
// Steel Frame structure type value = "3"
// Truss = "4", Composite Wood Joist = "5", Solid = "6", Panel = "7"
export const FRAMING = {
    // Wood Frame: [spacingCode][typeSizeCode]
    wood: {
        // 305 mm (12 in) spacing = code 0
        0: {
            0: { emissions: 0.9318, storage: 35.3241 },
            1: { emissions: 1.4658, storage: 55.566 },
            2: { emissions: 1.9264, storage: 73.0296 },
            3: { emissions: 2.4604, storage: 93.2715 },
            4: { emissions: 2.9943, storage: 113.5134 },
            5: { emissions: 1.0679, storage: 40.4838 },
        },
        // 400 mm (16 in) spacing = code 1
        1: {
            0: { emissions: 0.8748, storage: 33.1614 },
            1: { emissions: 1.376, storage: 52.164 },
            2: { emissions: 1.8085, storage: 68.5584 },
            3: { emissions: 2.3097, storage: 87.561 },
            4: { emissions: 2.811, storage: 106.5636 },
            5: { emissions: 1.0025, storage: 38.0052 },
        },
        // 487 mm (19 in) spacing = code 2
        2: {
            0: { emissions: 0.8177, storage: 30.9987 },
            1: { emissions: 1.2863, storage: 48.762 },
            2: { emissions: 1.6905, storage: 64.0872 },
            3: { emissions: 2.1591, storage: 81.8505 },
            4: { emissions: 2.6277, storage: 99.6138 },
            5: { emissions: 0.9371, storage: 35.5266 },
        },
        // 600 mm (24 in) spacing = code 3
        3: {
            0: { emissions: 0.7607, storage: 28.836 },
            1: { emissions: 1.1965, storage: 45.36 },
            2: { emissions: 1.5726, storage: 59.616 },
            3: { emissions: 2.0085, storage: 76.14 },
            4: { emissions: 2.4443, storage: 92.664 },
            5: { emissions: 0.8718, storage: 33.048 },
        },
    },
    // Wood Frame Normalized, m3 wood per m2 of wall to be multiplied by different Canadian averages
    woodNormalized: {
        // 305 mm (12 in) spacing = code 0
        0: {
            0: { m3perm2: 0.02181 },
            1: { m3perm2: 0.0343 },
            2: { m3perm2: 0.04508 },
            3: { m3perm2: 0.05758 },
            4: { m3perm2: 0.07007 },
            5: { m3perm2: 0.02499 },
        },
        // 400 mm (16 in) spacing = code 1
        1: {
            0: { m3perm2: 0.02047 },
            1: { m3perm2: 0.0322 },
            2: { m3perm2: 0.04232 },
            3: { m3perm2: 0.05405 },
            4: { m3perm2: 0.06578 },
            5: { m3perm2: 0.02346 },
        },
        // 487 mm (19 in) spacing = code 2
        2: {
            0: { m3perm2: 0.01914 },
            1: { m3perm2: 0.0301 },
            2: { m3perm2: 0.03956 },
            3: { m3perm2: 0.05053 },
            4: { m3perm2: 0.06149 },
            5: { m3perm2: 0.02193 },
        },
        // 600 mm (24 in) spacing = code 3
        3: {
            0: { m3perm2: 0.0178 },
            1: { m3perm2: 0.028 },
            2: { m3perm2: 0.0368 },
            3: { m3perm2: 0.047 },
            4: { m3perm2: 0.0572 },
            5: { m3perm2: 0.0204 },
        },
    },
    // Steel Frame
    steel: {
        0: {
            0: { emissions: 22.0803, storage: 0.0 },
            1: { emissions: 29.6932, storage: 0.0 },
        },
        1: {
            0: { emissions: 20.0225, storage: 0.0 },
            1: { emissions: 26.7946, storage: 0.0 },
        },
        2: {
            0: { emissions: 18.1381, storage: 0.0 },
            1: { emissions: 24.6206, storage: 0.0 },
        },
        3: {
            0: { emissions: 15.6905, storage: 0.0 },
            1: { emissions: 20.9973, storage: 0.0 },
        },
    },
    // Truss
    truss: {
        default: {
            0: { emissions: 3.3782, storage: 12.1706 }, // Flat truss 38x451
            1: { emissions: 3.3782, storage: 12.1706 }, // Flat truss 38x356
        },
    },
    // Composite Wood Joist
    compositeWoodJoist: {
        0: {
            0: { emissions: 5.5292, storage: 21.1013 },
            1: { emissions: 6.1442, storage: 23.4485 },
            2: { emissions: 6.6945, storage: 25.5485 },
            3: { emissions: 7.2124, storage: 27.525 },
        },
        1: {
            0: { emissions: 4.7393, storage: 18.0869 },
            1: { emissions: 5.2665, storage: 20.0987 },
            2: { emissions: 5.7381, storage: 21.8987 },
            3: { emissions: 6.1821, storage: 23.5929 },
        },
        2: {
            0: { emissions: 4.1469, storage: 15.826 },
            1: { emissions: 4.6082, storage: 17.5863 },
            2: { emissions: 5.0209, storage: 19.1614 },
            3: { emissions: 5.4093, storage: 20.6438 },
        },
        3: {
            0: { emissions: 3.1595, storage: 12.0579 },
            1: { emissions: 3.511, storage: 13.3991 },
            2: { emissions: 3.8254, storage: 14.5992 },
            3: { emissions: 4.1214, storage: 15.7286 },
        },
    },
    // Solid structure
    solid: {
        0: { emissions: 20.8537, storage: 0.0 },
        1: { emissions: 55.6097, storage: 0.0 },
        2: { emissions: 83.4146, storage: 0.0 },
        3: { emissions: 55.6097, storage: 0.0 },
        4: { emissions: 83.4146, storage: 0.0 },
        5: { emissions: 38.3863, storage: 0.0 }, // Insulating concrete block - 10" thick, filled with EPS. Using the average of Type I and Type II
        6: { emissions: 72.9053, storage: 0.0 },
        7: { emissions: 47.899, storage: 0.0 },
        8: { emissions: 53.1125, storage: 0.0 },
        D: { emissions: null, storage: null }, // Stone
        F: { emissions: 94.4969, storage: 0.0 }, // Double brick
        G: { emissions: null, storage: null }, // Quartzitic sandstone
        H: { emissions: null, storage: null }, // Calcitic limestone
        9: { emissions: 10.2366, storage: 388.0652 }, // 305mm round log
        B: { emissions: 8.5249, storage: 323.1756 }, // 254mm round log
        C: { emissions: 13.6264, storage: 516.5721 }, // 406mm round log
        E: { emissions: 4.3588, storage: 165.24 }, // 102mm plank
        A: { emissions: 6.41, storage: 243.0 }, // 150mm rectangular log
    },
    // Panel (SIP)
    panel: {
        0: { emissions: 19.9869, storage: 21.56 },
        1: { emissions: 20.0162, storage: 21.56 },
        2: { emissions: 18.4632, storage: 21.56 },
        3: { emissions: 20.485, storage: 21.56 },
        4: { emissions: 24.4114, storage: 21.56 },
        5: { emissions: 19.4301, storage: 21.56 },
        6: { emissions: 23.6203, storage: 21.56 },
    },
};

export const BASEMENT_WALL_FRAMING = {
    wood: {
        // 305 mm (12 in) spacing = code 0
        0: {
            1: { emissions: 0.6701, storage: 25.4016 },
            2: { emissions: 0.9318, storage: 35.3241 },
            3: { emissions: 1.4658, storage: 55.566 },
            4: { emissions: 1.9264, storage: 73.0296 },
            5: { emissions: 2.4604, storage: 93.2715 },
            5: { emissions: 2.9943, storage: 113.5134 },
        },
        // 400 mm (16 in) spacing = code 1
        1: {
            1: { emissions: 0.629, storage: 23.8464 },
            2: { emissions: 0.8748, storage: 33.1614 },
            3: { emissions: 1.376, storage: 52.164 },
            4: { emissions: 1.8085, storage: 68.5584 },
            5: { emissions: 2.3097, storage: 87.561 },
            5: { emissions: 2.811, storage: 106.5636 },
        },
        // 487 mm (19 in) spacing = code 2
        2: {
            1: { emissions: 0.588, storage: 22.2912 },
            2: { emissions: 0.8177, storage: 30.9987 },
            3: { emissions: 1.2863, storage: 48.762 },
            4: { emissions: 1.6905, storage: 64.0872 },
            5: { emissions: 2.1591, storage: 81.8505 },
            5: { emissions: 2.6277, storage: 99.6138 },
        },
        // 600 mm (24 in) spacing = code 3
        3: {
            1: { emissions: 0.547, storage: 20.736 },
            2: { emissions: 0.7607, storage: 28.836 },
            3: { emissions: 1.1965, storage: 45.36 },
            4: { emissions: 1.5726, storage: 59.616 },
            5: { emissions: 2.0085, storage: 76.14 },
            5: { emissions: 2.4443, storage: 92.664 },
        },
    },
    woodNormalized: {
        // 305 mm (12 in) spacing = code 0
        0: {
            1: { emissions: 0.01568, storage: 0.01568 },
            2: { emissions: 0.02181, storage: 0.02181 },
            3: { emissions: 0.0343, storage: 0.0343 },
            4: { emissions: 0.04508, storage: 0.04508 },
            5: { emissions: 0.05758, storage: 0.05758 },
            5: { emissions: 0.07007, storage: 0.07007 },
        },
        // 400 mm (16 in) spacing = code 1
        1: {
            1: { emissions: 0.01472, storage: 0.01472 },
            2: { emissions: 0.02047, storage: 0.02047 },
            3: { emissions: 0.0322, storage: 0.0322 },
            4: { emissions: 0.04232, storage: 0.04232 },
            5: { emissions: 0.05405, storage: 0.05405 },
            5: { emissions: 0.06578, storage: 0.06578 },
        },
        // 487 mm (19 in) spacing = code 2
        2: {
            1: { emissions: 0.01376, storage: 0.01376 },
            2: { emissions: 0.01914, storage: 0.01914 },
            3: { emissions: 0.0301, storage: 0.0301 },
            4: { emissions: 0.03956, storage: 0.03956 },
            5: { emissions: 0.05053, storage: 0.05053 },
            5: { emissions: 0.06149, storage: 0.06149 },
        },
        // 600 mm (24 in) spacing = code 3
        3: {
            1: { emissions: 0.0128, storage: 0.0128 },
            2: { emissions: 0.0178, storage: 0.0178 },
            3: { emissions: 0.028, storage: 0.028 },
            4: { emissions: 0.0368, storage: 0.0368 },
            5: { emissions: 0.047, storage: 0.047 },
            5: { emissions: 0.0572, storage: 0.0572 },
        },
    },
    steel: {
        //All use 3.6in
        0: {
            7: { emissions: 17.8159, storage: 0.0 },
            8: { emissions: 17.8159, storage: 0.0 },
        },
        1: {
            7: { emissions: 16.0768, storage: 0.0 },
            8: { emissions: 16.0768, storage: 0.0 },
        },
        2: {
            7: { emissions: 14.7724, storage: 0.0 },
            8: { emissions: 14.7724, storage: 0.0 },
        },
        3: {
            7: { emissions: 12.5984, storage: 0.0 },
            8: { emissions: 12.5984, storage: 0.0 },
        },
    },
};

// Wood averages in kgCO2e/m3 for different Canadian provinces
export const WOOD_AVERAGES = {
    BC: { emissions: 41.5, storage: 1422.0, source: "BC" },
    AB: { emissions: 57, storage: 1512.0, source: "AB" },
    ON: { emissions: 54.6, storage: 1782.0, source: "ON" },
    QC: { emissions: 24.8, storage: 1656.0, source: "QC" },
    NB: { emissions: 41.4, storage: 1746.0, source: "NB" },

    NS: { emissions: 37.1, storage: 1602.0, source: "Atlantic Canada" }, //ATL
    PE: { emissions: 37.1, storage: 1602.0, source: "Atlantic Canada" }, //ATL
    NL: { emissions: 37.1, storage: 1602.0, source: "Atlantic Canada" }, //ATL

    SK: { emissions: 42.733, storage: 1620.0, source: "Canadian" }, //CAN AVERAGE
    MB: { emissions: 42.733, storage: 1620.0, source: "Canadian" }, //CAN AVERAGE
    YT: { emissions: 42.733, storage: 1620.0, source: "Canadian" }, //CAN AVERAGE
    NT: { emissions: 42.733, storage: 1620.0, source: "Canadian" }, //CAN AVERAGE
    NU: { emissions: 42.733, storage: 1620.0, source: "Canadian" }, //CAN AVERAGE

    CAN: { emissions: 42.733, storage: 1620.0, source: "Canadian" }, //CAN AVERAGE, default
};

/** National average key in {@link WOOD_AVERAGES}; used as the baseline for provincial scaling. */
export const DEFAULT_WOOD_PROVINCE_KEY = "CAN";

/** Suffix for {@link getWoodFramingDefaultSourceLabel}; leading word comes from {@link WOOD_AVERAGES}[key].source. */
const WOOD_FRAMING_SOURCE_LABEL_SUFFIX = " average, including spacing and size assumptions (2026).";

/**
 * Default citation line for wood-framed framing_embC: `[region source] average, including spacing and size assumptions (2026).`
 * National (CAN) uses "Canadian" from {@link WOOD_AVERAGES}.CAN.source; other keys use that region’s `source` (e.g. "BC", "Atlantic Canada").
 */
export function getWoodFramingDefaultSourceLabel(provinceKey) {
    const key =
        provinceKey != null && provinceKey !== "" && WOOD_AVERAGES[provinceKey]
            ? provinceKey
            : DEFAULT_WOOD_PROVINCE_KEY;
    const row = WOOD_AVERAGES[key];
    const word = row?.source ?? WOOD_AVERAGES[DEFAULT_WOOD_PROVINCE_KEY]?.source ?? "Canadian";
    return `${word}${WOOD_FRAMING_SOURCE_LABEL_SUFFIX}`;
}

/** Keys for UI dropdowns (CAN first, then A–Z). */
export const WOOD_AVERAGE_REGION_KEYS = Object.keys(WOOD_AVERAGES).sort((a, b) => {
    if (a === DEFAULT_WOOD_PROVINCE_KEY) return -1;
    if (b === DEFAULT_WOOD_PROVINCE_KEY) return 1;
    return a.localeCompare(b);
});

/**
 * Scale map-derived framing emissions/storage using regional wood intensity vs national CAN average.
 * emissions' = emissions × (target.emissions / CAN.emissions), storage' = storage × (target.storage / CAN.storage).
 */
export const scaleFramingByWoodProvinceKey = (baseEmissions, baseStorage, provinceKey) => {
    const CAN = WOOD_AVERAGES.CAN;
    if (!CAN || !CAN.emissions || !CAN.storage) {
        return { emissions: baseEmissions, storage: baseStorage ?? 0 };
    }
    if (provinceKey == null || provinceKey === "" || provinceKey === DEFAULT_WOOD_PROVINCE_KEY) {
        return { emissions: baseEmissions, storage: baseStorage ?? 0 };
    }
    const target = WOOD_AVERAGES[provinceKey];
    if (!target) {
        return { emissions: baseEmissions, storage: baseStorage ?? 0 };
    }
    return {
        emissions: baseEmissions != null ? (baseEmissions / CAN.emissions) * target.emissions : baseEmissions,
        storage: baseStorage != null ? (baseStorage / CAN.storage) * target.storage : (baseStorage ?? 0),
    };
};

//Framing and cavity percentages for wood framed walls, keyed to spacing ID
//componentType must match the keys below
export const FRAME_CAVITY_FRACTIONS = {
    wall: {
        2: {
            0: { framingArea: 24.5, cavityArea: 75.5 },
            1: { framingArea: 23.0, cavityArea: 77.0 },
            2: { framingArea: 21.5, cavityArea: 78.5 },
            3: { framingArea: 20.0, cavityArea: 80.0 },
        },
        3: {
            0: { framingArea: 0.77, cavityArea: 99.23 },
            1: { framingArea: 0.77, cavityArea: 99.23 },
            2: { framingArea: 0.77, cavityArea: 99.23 },
            3: { framingArea: 0.67, cavityArea: 99.33 },
        },
        4: {
            0: { framingArea: 10.4, cavityArea: 89.6 },
            1: { framingArea: 9.0, cavityArea: 91.0 },
            2: { framingArea: 7.5, cavityArea: 92.5 },
            3: { framingArea: 6.0, cavityArea: 94.0 },
        },
        5: {
            0: { framingArea: 10.4, cavityArea: 89.6 },
            1: { framingArea: 9.0, cavityArea: 91.0 },
            2: { framingArea: 7.5, cavityArea: 92.5 },
            3: { framingArea: 6.0, cavityArea: 94.0 },
        },
        6: {
            0: { framingArea: 0, cavityArea: 100 },
            1: { framingArea: 20, cavityArea: 80 },
            2: { framingArea: 20, cavityArea: 80 },
        },
        7: {},
    },
    crawlspaceWall: {
        2: {
            0: { framingArea: 24.5, cavityArea: 75.5 },
            1: { framingArea: 23.0, cavityArea: 77.0 },
            2: { framingArea: 21.5, cavityArea: 78.5 },
            3: { framingArea: 20.0, cavityArea: 80.0 },
        },
        3: {
            0: { framingArea: 0.77, cavityArea: 99.23 },
            1: { framingArea: 0.77, cavityArea: 99.23 },
            2: { framingArea: 0.77, cavityArea: 99.23 },
            3: { framingArea: 0.67, cavityArea: 99.33 },
        },
        4: {
            0: { framingArea: 10.4, cavityArea: 89.6 },
            1: { framingArea: 9.0, cavityArea: 91.0 },
            2: { framingArea: 7.5, cavityArea: 92.5 },
            3: { framingArea: 6.0, cavityArea: 94.0 },
        },
        5: {
            0: { framingArea: 10.4, cavityArea: 89.6 },
            1: { framingArea: 9.0, cavityArea: 91.0 },
            2: { framingArea: 7.5, cavityArea: 92.5 },
            3: { framingArea: 6.0, cavityArea: 94.0 },
        },
        6: {
            0: { framingArea: 0, cavityArea: 100 },
            1: { framingArea: 20, cavityArea: 80 },
            2: { framingArea: 20, cavityArea: 80 },
        },
        7: {},
    },
    expFloor: {
        2: {
            0: { framingArea: 14.4, cavityArea: 85.6 },
            1: { framingArea: 13.0, cavityArea: 87.0 },
            2: { framingArea: 11.5, cavityArea: 88.5 },
            3: { framingArea: 10.0, cavityArea: 90.0 },
        },
        3: {
            0: { framingArea: 0.43, cavityArea: 99.57 },
            1: { framingArea: 0.43, cavityArea: 99.57 },
            2: { framingArea: 0.43, cavityArea: 99.57 },
            3: { framingArea: 0.33, cavityArea: 99.67 },
        },
        4: {
            0: { framingArea: 10.4, cavityArea: 89.6 },
            1: { framingArea: 9.0, cavityArea: 91.0 },
            2: { framingArea: 7.5, cavityArea: 92.5 },
            3: { framingArea: 6.0, cavityArea: 94.0 },
        },
        5: {
            0: { framingArea: 10.4, cavityArea: 89.6 },
            1: { framingArea: 9.0, cavityArea: 91.0 },
            2: { framingArea: 7.5, cavityArea: 92.5 },
            3: { framingArea: 6.0, cavityArea: 94.0 },
        },
        6: {
            0: { framingArea: 0, cavityArea: 100 },
            1: { framingArea: 10, cavityArea: 90 },
            2: { framingArea: 10, cavityArea: 90 },
        },
        7: {},
    },
    floorsAbove: {
        2: {
            0: { framingArea: 14.4, cavityArea: 85.6 },
            1: { framingArea: 13.0, cavityArea: 87.0 },
            2: { framingArea: 11.5, cavityArea: 88.5 },
            3: { framingArea: 10.0, cavityArea: 90.0 },
        },
        3: {
            0: { framingArea: 0.43, cavityArea: 99.57 },
            1: { framingArea: 0.43, cavityArea: 99.57 },
            2: { framingArea: 0.43, cavityArea: 99.57 },
            3: { framingArea: 0.33, cavityArea: 99.67 },
        },
        4: {
            0: { framingArea: 10.4, cavityArea: 89.6 },
            1: { framingArea: 9.0, cavityArea: 91.0 },
            2: { framingArea: 7.5, cavityArea: 92.5 },
            3: { framingArea: 6.0, cavityArea: 94.0 },
        },
        5: {
            0: { framingArea: 10.4, cavityArea: 89.6 },
            1: { framingArea: 9.0, cavityArea: 91.0 },
            2: { framingArea: 7.5, cavityArea: 92.5 },
            3: { framingArea: 6.0, cavityArea: 94.0 },
        },
        6: {
            0: { framingArea: 0, cavityArea: 100 },
            1: { framingArea: 10, cavityArea: 90 },
            2: { framingArea: 10, cavityArea: 90 },
        },
        7: {},
    },
    ceiling: {
        2: {
            0: { framingArea: 14.4, cavityArea: 85.6 },
            1: { framingArea: 13.0, cavityArea: 87.0 },
            2: { framingArea: 11.5, cavityArea: 88.5 },
            3: { framingArea: 10.0, cavityArea: 90.0 },
        },
        3: {
            0: { framingArea: 0.43, cavityArea: 99.57 },
            1: { framingArea: 0.43, cavityArea: 99.57 },
            2: { framingArea: 0.43, cavityArea: 99.57 },
            3: { framingArea: 0.33, cavityArea: 99.67 },
        },
        4: {
            0: { framingArea: 15.4, cavityArea: 84.6 },
            1: { framingArea: 14.0, cavityArea: 86.0 },
            2: { framingArea: 12.5, cavityArea: 87.5 },
            3: { framingArea: 11.0, cavityArea: 89.0 },
        },
        5: {
            0: { framingArea: 10.4, cavityArea: 89.6 },
            1: { framingArea: 9.0, cavityArea: 91.0 },
            2: { framingArea: 7.5, cavityArea: 92.5 },
            3: { framingArea: 6.0, cavityArea: 94.0 },
        },
        6: {
            0: { framingArea: 0, cavityArea: 100 },
            1: { framingArea: 10, cavityArea: 90 },
            2: { framingArea: 10, cavityArea: 90 },
        },
        7: {},
    },
    ceilingFlat: {
        2: {
            0: { framingArea: 14.4, cavityArea: 85.6 },
            1: { framingArea: 13.0, cavityArea: 87.0 },
            2: { framingArea: 11.5, cavityArea: 88.5 },
            3: { framingArea: 10.0, cavityArea: 90.0 },
        },
        3: {
            0: { framingArea: 0.43, cavityArea: 99.57 },
            1: { framingArea: 0.43, cavityArea: 99.57 },
            2: { framingArea: 0.43, cavityArea: 99.57 },
            3: { framingArea: 0.33, cavityArea: 99.67 },
        },
        4: {
            0: { framingArea: 15.4, cavityArea: 84.6 },
            1: { framingArea: 14.0, cavityArea: 86.0 },
            2: { framingArea: 12.5, cavityArea: 87.5 },
            3: { framingArea: 11.0, cavityArea: 89.0 },
        },
        5: {
            0: { framingArea: 10.4, cavityArea: 89.6 },
            1: { framingArea: 9.0, cavityArea: 91.0 },
            2: { framingArea: 7.5, cavityArea: 92.5 },
            3: { framingArea: 6.0, cavityArea: 94.0 },
        },
        6: {
            0: { framingArea: 0, cavityArea: 100 },
            1: { framingArea: 10, cavityArea: 90 },
            2: { framingArea: 10, cavityArea: 90 },
        },
        7: {},
    },
    basementWall: {
        wood: {
            0: { framingArea: 24.5, cavityArea: 75.5 },
            1: { framingArea: 23.0, cavityArea: 77.0 },
            2: { framingArea: 21.5, cavityArea: 78.5 },
            3: { framingArea: 20.0, cavityArea: 80.0 },
        },
        steel: {
            0: { framingArea: 0.77, cavityArea: 99.23 },
            1: { framingArea: 0.77, cavityArea: 99.23 },
            2: { framingArea: 0.77, cavityArea: 99.23 },
            3: { framingArea: 0.67, cavityArea: 99.33 },
        },
    },
};

// -----------------------------------------------------------------------------
// Grading: shared scales, EPD-vs-map rules, framing, windows
// -----------------------------------------------------------------------------
// Maps structure type value to framing data key
const STRUCTURE_TYPE_MAP = {
    2: "wood",
    3: "steel",
    4: "truss",
    5: "compositeWoodJoist",
    6: "solid",
    7: "panel",
};

/**
 * Get the emission grade label for a given numeric grade value (0 to 1).
 * @param {number} gradeValue - numeric grade from 0 (best, A+) to 0.9 (worst, D)
 * @returns {string} grade label
 */
export const getGradeLabel = (gradeValue) => {
    if (gradeValue == null || isNaN(gradeValue)) return "N/A";

    const grade = GRADE_SCALE.find((g) => Math.abs(g.value - gradeValue) < 0.001);

    return grade ? grade.label : "N/A";
};

/**
 * Get the color for a given grade label.
 * @param {string} gradeLabel
 * @returns {string} hex color
 */
export const getGradeColor = (gradeLabel) => GRADE_COLORS[gradeLabel] || GRADE_COLORS["N/A"];

/**
 * Get the numeric grade value for a given grade label.
 * @param {string} label - grade label like "A+", "B", "C-", etc.
 * @returns {number|null}
 */
export const getGradeValue = (label) => {
    if (!label || label === "N/A") return null;

    const grade = GRADE_SCALE.find((g) => g.label === label);

    return grade ? grade.value : null;
};

/**
 * Get framing/structure embodied carbon data for a given code.
 * @param {object} layers - code layers object
 * @returns {{ emissions: number|null, storage: number|null }}
 */
const getFramingCarbon = (layers) => {
    const { structureType = {}, componentTypeSize = {}, spacing = {} } = layers;

    const structureValue = structureType.value;
    const structureKey = STRUCTURE_TYPE_MAP[structureValue];

    if (!structureKey) return { emissions: null, storage: null };

    const framingData = FRAMING[structureKey];
    if (!framingData) return { emissions: null, storage: null };

    const typeSizeCode = (componentTypeSize.value || "0").toString();
    const spacingCode = (spacing.value || "0").toString();

    // Solid and Panel don't use spacing - index directly by typeSize
    if (structureKey === "solid" || structureKey === "panel") {
        return framingData[typeSizeCode] || { emissions: null, storage: null };
    }

    // Truss uses default spacing
    if (structureKey === "truss") {
        const spacingData = framingData.default || {};

        return spacingData[typeSizeCode] || { emissions: null, storage: null };
    }

    // Wood, Steel, Composite use spacing -> typeSize
    const spacingData = framingData[spacingCode];

    if (!spacingData) return { emissions: null, storage: null };

    return spacingData[typeSizeCode] || { emissions: null, storage: null };
};

/** Map-only framing totals (no framing_embC); used for provincial wood scaling in the UI. */
export const getRawMapFramingCarbon = (layers, componentType = "wall") =>
    componentType === "basementWall" ? getBasementWallFramingCarbon(layers) : getFramingCarbon(layers);

/** True when structure type is wood frame (standard wall / compatible codes). */
export const isWoodFramedWallLayers = (layers) => {
    const v = layers?.structureType?.value;
    return STRUCTURE_TYPE_MAP[v] === "wood";
};

const getBasementWallFramingCarbon = (layers) => {
    const { framing = {}, spacing = {} } = layers;
    const framingCode = (framing.value || "0").toString();

    if (framingCode === "0") {
        return { emissions: null, storage: null };
    }

    const framingMaterial = ["1", "2", "3", "4", "5", "6"].includes(framingCode) ? "wood" : "steel";

    const spacingCode = (spacing.value || "0").toString();
    const spacingData = FRAME_CAVITY_FRACTIONS["basementWall"]?.[framingMaterial]?.[spacingCode] || {
        emissions: null,
        storage: null,
    };

    const framingData = BASEMENT_WALL_FRAMING?.[framingMaterial]?.[spacingCode]?.[framingCode] || {};

    if (!framingData) return { emissions: null, storage: null };

    return framingData;
};

/** Suffix for user-defined embodied carbon fields on a layer (e.g. insulationLayer1_embC). */
export const LAYER_EMB_C_SUFFIX = "_embC";

export const getLayerEmbCarbonFieldKey = (layerType) => `${layerType}${LAYER_EMB_C_SUFFIX}`;

/**
 * Wall: when spacing select is unset (e.g. solid), cavity fraction uses framing spacing code.
 */
export const getEffectiveWallSpacingCodeForCavity = (layers) => {
    if (layers?.spacing?.id == null) {
        return (layers?.framing?.value || "0").toString();
    }
    return (layers?.spacing?.value || "0").toString();
};

/**
 * Cavity area percent (0–100) for insulation layer 1 from current framing fields.
 */
export const getCavityAreaPercentForInsulationLayer1 = (layers, componentType = "wall") => {
    if (componentType === "basementWall") {
        const framingCode = (layers?.framing?.value || "0").toString();
        const basementWallFramingType = ["0", "1", "2", "3", "4", "5", "6"].includes(framingCode) ? "wood" : "steel";
        const spacingCode = (layers?.spacing?.value || "0").toString();
        return FRAME_CAVITY_FRACTIONS[componentType]?.[basementWallFramingType]?.[spacingCode]?.cavityArea ?? 100;
    }
    const structureCode = (layers?.structureType?.value || "0").toString();
    const spacingCode =
        componentType === "wall"
            ? getEffectiveWallSpacingCodeForCavity(layers)
            : (layers?.spacing?.value || "0").toString();
    return FRAME_CAVITY_FRACTIONS?.[componentType]?.[structureCode]?.[spacingCode]?.cavityArea ?? 100;
};

/**
 * Custom EPD (`*_embC`) vs **same HOT2000 row** map default: ratio bands (simpler than {@link getMaterialLayerEmissionGrade}).
 * Used for insulation layers and any layer without a material benchmark. r = emissions / mapDefault.
 */
const RELATIVE_EMISSION_TO_MAP_TIERS = [
    [0.5, "A+"],
    [0.7, "A"],
    [1.0, "B"],
    [2.0, "C"],
    [4.0, "D"],
];

export const getRelativeEmissionsGrade = (emissions, benchmark) => {
    if (emissions == null || benchmark == null || benchmark <= 0) return "N/A";
    if (emissions === 0) return "N/A";
    const r = emissions / benchmark;
    for (const [maxR, grade] of RELATIVE_EMISSION_TO_MAP_TIERS) {
        if (r <= maxR) return grade;
    }
    return "D";
};

/**
 * Placeholder: relative storage grade vs benchmark (higher storage = better). Requires manual benchmarks.
 */
export const getRelativeStorageGrade = (storage, benchmark) => {
    void storage;
    void benchmark;
    return "N/A";
};

/** Fallback absolute kgCO₂e/m² bands (e.g. non-wood framing interpolated grade, or last resort in EPD helper). */
const OVERALL_STRUCTURE_EMISSION_TIERS = [
    [2, "A+"],
    [5, "A"],
    [8, "A-"],
    [12, "B+"],
    [18, "B"],
    [25, "B-"],
    [35, "C+"],
    [50, "C"],
    [70, "C-"],
];

export const getOverallStructureTypeEmissionGrade = (totalEmissions) => {
    if (totalEmissions == null) return "N/A";
    if (totalEmissions === 0) return "N/A";
    for (const [maxE, grade] of OVERALL_STRUCTURE_EMISSION_TIERS) {
        if (totalEmissions <= maxE) return grade;
    }
    return "D";
};

/** @param {number} totalEmissions */
const getOverallEmissionGrade = (totalEmissions) => getOverallStructureTypeEmissionGrade(totalEmissions);

// --- Framing-only emission & storage grades (wood: anchors + band; steel/other: min–max from FRAMING map) ---

/** Upper emissions bound (kgCO₂e/m²) for wood framing bands A-…D (worst ≈ this value). */
export const FRAMING_WOOD_EMISSION_WORST_BOUND = 80;

const FRAMING_WOOD_EMISSION_A_PLUS_MAX = 2;
const FRAMING_WOOD_EMISSION_A_MAX = 3.5;

const FRAMING_WOOD_EMISSION_HIGH_TIERS = ["A-", "B+", "B", "B-", "C+", "C", "C-", "D"];

/**
 * Wood framing (wall or basement wood): emissions &lt; 2 → A+; &lt; 3.5 → A; then A-…D scale from 3.5 to {@link FRAMING_WOOD_EMISSION_WORST_BOUND}.
 */
export function getFramingWoodEmissionGrade(emissions) {
    if (emissions == null || Number.isNaN(emissions)) return "N/A";
    if (emissions < FRAMING_WOOD_EMISSION_A_PLUS_MAX) return "A+";
    if (emissions < FRAMING_WOOD_EMISSION_A_MAX) return "A";
    if (emissions >= FRAMING_WOOD_EMISSION_WORST_BOUND) return "D";
    const span = FRAMING_WOOD_EMISSION_WORST_BOUND - FRAMING_WOOD_EMISSION_A_MAX;
    if (span <= 0) return "D";
    const t = (emissions - FRAMING_WOOD_EMISSION_A_MAX) / span;
    const idx = Math.min(
        FRAMING_WOOD_EMISSION_HIGH_TIERS.length - 1,
        Math.floor(t * FRAMING_WOOD_EMISSION_HIGH_TIERS.length)
    );
    return FRAMING_WOOD_EMISSION_HIGH_TIERS[idx];
}

/**
 * Wood framing storage (kgCO₂/m²): &gt;100 A+; &gt;50 A; &gt;20 B+; 0 → N/A (no bar); (0,20] scaled C-…B-; higher is better above 20.
 */
export function getFramingWoodStorageGrade(storage) {
    const s = storage ?? 0;
    if (s < 0) return "N/A";
    if (s === 0) return "N/A";
    if (s > 100) return "A+";
    if (s > 50) return "A";
    if (s > 20) return "B+";
    const tiers = ["C-", "C", "C+", "B-"];
    const i = Math.min(tiers.length - 1, Math.floor((s / 20) * tiers.length));
    return tiers[i];
}

/** Basement wall: wood framing codes 1–6; otherwise use {@link isWoodFramedWallLayers}. */
export function isWoodFramingForEmbodiedCarbon(layers, componentType = "wall") {
    if (componentType === "basementWall") {
        const framingCode = (layers?.framing?.value || "0").toString();
        return ["1", "2", "3", "4", "5", "6"].includes(framingCode);
    }
    return isWoodFramedWallLayers(layers);
}

/**
 * Walks a nested framing map (e.g. FRAMING.wood spacing → type-size leaves) and collects every
 * object that has numeric `emissions` and `storage` — the embodied-carbon rows used for min/max
 * bounds. Recurses into child objects but skips keys in `skipKeys` (e.g. `woodNormalized`, which
 * holds m³/m² factors rather than kg/m² totals). Results are appended to `out`.
 */
function collectFramingEmissionStorageLeaves(node, skipKeys, out) {
    if (!node || typeof node !== "object") return;
    if (typeof node.emissions === "number" && typeof node.storage === "number") {
        out.push(node);
        return;
    }
    for (const [k, v] of Object.entries(node)) {
        if (skipKeys.has(k)) continue;
        collectFramingEmissionStorageLeaves(v, skipKeys, out);
    }
}

/**
 * Min/max emissions and max storage across all framing map entries for the current structure (for non-wood dynamic grades).
 */
export function getFramingCategoryBounds(layers, componentType = "wall") {
    if (componentType === "basementWall") {
        const framingCode = (layers?.framing?.value || "0").toString();
        if (framingCode === "0") return { minE: null, maxE: null, maxS: null };
        const material = ["1", "2", "3", "4", "5", "6"].includes(framingCode) ? "wood" : "steel";
        const block = BASEMENT_WALL_FRAMING[material];
        if (!block) return { minE: null, maxE: null, maxS: null };
        const leaves = [];
        collectFramingEmissionStorageLeaves(block, new Set(["woodNormalized"]), leaves);
        return boundsFromFramingLeaves(leaves);
    }
    const structureValue = layers?.structureType?.value;
    const structureKey = STRUCTURE_TYPE_MAP[structureValue];
    if (!structureKey) return { minE: null, maxE: null, maxS: null };
    const block = FRAMING[structureKey];
    if (!block) return { minE: null, maxE: null, maxS: null };
    const leaves = [];
    collectFramingEmissionStorageLeaves(block, new Set(["woodNormalized"]), leaves);
    return boundsFromFramingLeaves(leaves);
}

function boundsFromFramingLeaves(leaves) {
    if (!leaves.length) return { minE: null, maxE: null, maxS: null };
    const es = leaves.map((l) => l.emissions).filter((n) => n != null && Number.isFinite(n));
    const ss = leaves.map((l) => l.storage ?? 0).filter((n) => Number.isFinite(n));
    if (!es.length) return { minE: null, maxE: null, maxS: null };
    return {
        minE: Math.min(...es),
        maxE: Math.max(...es),
        maxS: ss.length ? Math.max(...ss) : null,
    };
}

function getFramingDynamicEmissionGrade(emissions, minE, maxE) {
    if (emissions == null || Number.isNaN(emissions)) return "N/A";
    if (minE == null || maxE == null) return getOverallStructureTypeEmissionGrade(emissions);
    const span = maxE - minE;
    if (span <= 0) return emissions <= minE ? "A+" : "D";
    const t = (emissions - minE) / span;
    const clamped = Math.max(0, Math.min(1, t));
    const idx = Math.min(GRADE_SCALE.length - 1, Math.floor(clamped * GRADE_SCALE.length));
    return GRADE_SCALE[idx].label;
}

function getFramingDynamicStorageGrade(storage, maxS) {
    if (storage == null || Number.isNaN(storage)) return "N/A";
    if (storage <= 0) return "N/A";
    if (maxS == null || maxS <= 0) return "N/A";
    const t = Math.min(1, Math.max(0, storage / maxS));
    const idx = Math.min(STORAGE_GRADE_SCALE.length - 1, Math.floor(t * STORAGE_GRADE_SCALE.length));
    return STORAGE_GRADE_SCALE[idx].label;
}

export function getFramingEmissionGrade(emissions, layers, componentType = "wall") {
    if (emissions == null || emissions === 0) return "N/A";
    if (isWoodFramingForEmbodiedCarbon(layers, componentType)) {
        return getFramingWoodEmissionGrade(emissions);
    }
    const { minE, maxE } = getFramingCategoryBounds(layers, componentType);
    return getFramingDynamicEmissionGrade(emissions, minE, maxE);
}

export function getFramingStorageGrade(storage, layers, componentType = "wall") {
    if (isWoodFramingForEmbodiedCarbon(layers, componentType)) {
        return getFramingWoodStorageGrade(storage);
    }
    const { maxS } = getFramingCategoryBounds(layers, componentType);
    return getFramingDynamicStorageGrade(storage, maxS);
}

/**
 * Emission grade for a layer when the user has entered custom EPD (`embRaw`) or we need a derived grade.
 *
 * 1. **Material benchmark layers** (interior, interiorFinish, exterior, sheathing): grade from ratio to
 *    {@link MATERIAL_EMISSIONS_BENCHMARKS} — independent of the map row’s static letter grade.
 * 2. Else **ratio to map default** ({@link getRelativeEmissionsGrade}) — for insulation & other layers with a map row.
 * 3. Else **absolute scale** ({@link getOverallStructureTypeEmissionGrade}) on `displayAdjusted` (scaled display value).
 * 4. Else **N/A**.
 */
export const emissionGradeFromEmbC = (layerType, embRaw, mapRaw, displayAdjusted) => {
    const materialBench = getMaterialEmissionsBenchmarkValue(layerType);
    if (materialBench != null && embRaw != null) {
        return getMaterialLayerEmissionGrade(embRaw, materialBench);
    }
    const rel = getRelativeEmissionsGrade(embRaw, mapRaw);
    if (rel !== "N/A") return rel;
    if (displayAdjusted != null) return getOverallEmissionGrade(displayAdjusted);
    return "N/A";
};

export const storageGradeFromEmbC = (embRaw, mapRaw, mapRow) => {
    const rel = getRelativeStorageGrade(embRaw, mapRaw);
    if (rel !== "N/A") return rel;
    return mapRow?.storageGrade ?? "N/A";
};

/**
 * Resolve display values for LayerGrade (defaults + optional layer_embC raw overrides).
 * Basement wall uses `interiorFinish`, `insulationFramingLayer`, and `extraInsulationLayer` as layer keys (see templates).
 */
export const getLayerEmbodiedCarbonDisplay = ({ layerType, layers, componentType = "wall" }) => {
    let map;
    let layerSelectKey = layerType;

    if (layerType === "interiorFinish") {
        map = INTERIOR;
        layerSelectKey = "interiorFinish";
    } else if (layerType === "insulationFramingLayer") {
        map = INSULATION_LAYER_1;
        layerSelectKey = "insulationFramingLayer";
    } else if (layerType === "extraInsulationLayer") {
        map = BASEMENT_WALL_EXTRA_INSULATION_LAYER;
        layerSelectKey = "extraInsulationLayer";
    } else {
        map = {
            insulationLayer1: INSULATION_LAYER_1,
            insulationLayer2: INSULATION_LAYER_2,
            interior: INTERIOR,
            sheathing: SHEATHING,
            exterior: componentType === "floorsAbove" ? INTERIOR : EXTERIOR,
        }[layerType];
    }

    const layerSelect = layers?.[layerSelectKey];
    const codeStr = (layerSelect?.value ?? "0").toString();
    const defaultRow = map?.[codeStr] || {};
    const usesInteriorExteriorAlts =
        layerType === "interior" || layerType === "interiorFinish" || layerType === "exterior";
    const materialAltField = usesInteriorExteriorAlts ? getLayerMaterialAltFieldKey(layerSelectKey) : null;
    const materialAltKeyFromLayers = materialAltField != null ? layers?.[materialAltField] : undefined;
    const mapRowForDisplay = usesInteriorExteriorAlts
        ? resolveInteriorExteriorMaterialRow(defaultRow, materialAltKeyFromLayers)
        : defaultRow;
    const embKey = getLayerEmbCarbonFieldKey(layerSelectKey);
    const embC = layers?.[embKey];

    const usesFramingCavity = layerType === "insulationLayer1" || layerType === "insulationFramingLayer";
    const cavityPct = usesFramingCavity ? getCavityAreaPercentForInsulationLayer1(layers, componentType) : 100;
    const cavityFraction = cavityPct / 100;

    const hasMismatch =
        embC != null &&
        embC.emissions != null &&
        (String(embC.layerCodeValue ?? "") !== String(layerSelect?.value ?? "") ||
            String(embC.materialAltKey ?? "") !== String(materialAltKeyFromLayers ?? ""));

    if (embC != null && embC.emissions != null) {
        const emissions = embC.emissions * cavityFraction;
        const storage = (embC.storage ?? 0) * cavityFraction;
        return {
            emissions,
            storage,
            emissionGrade: emissionGradeFromEmbC(layerType, embC.emissions, mapRowForDisplay.emissions, emissions),
            storageGrade: storageGradeFromEmbC(embC.storage, mapRowForDisplay.storage, mapRowForDisplay),
            defaultSource: mapRowForDisplay.source || "",
            hasMismatch,
            mismatchCodeLabel: embC.layerCodeValue,
        };
    }

    let emissions = mapRowForDisplay.emissions != null ? mapRowForDisplay.emissions * cavityFraction : null;
    let storage = mapRowForDisplay.storage != null ? mapRowForDisplay.storage * cavityFraction : null;

    // If cavity depth is required, multiply emissions and storage by the cavity depth
    // Note that we don't do this for user-defined values
    const structureValue = layers?.structureType?.value;
    const structureCode = (structureValue ?? "0").toString();
    const componentTypeSizeValue = layers?.componentTypeSize?.value;
    const componentTypeSizeCode = (componentTypeSizeValue ?? "0").toString();

    const depthKeyLayer = layerType === "insulationLayer1" || layerType === "insulationFramingLayer" ? layerType : null;
    if (
        depthKeyLayer &&
        CAVITY_DEPTHS[structureCode]?.[componentTypeSizeCode] != null &&
        mapRowForDisplay.perUnit === "m2/mm"
    ) {
        emissions *= CAVITY_DEPTHS[structureCode][componentTypeSizeCode];
        storage *= CAVITY_DEPTHS[structureCode][componentTypeSizeCode];
    }

    const materialBench = getMaterialEmissionsBenchmarkValue(layerType);
    const emissionGradeFromBenchmark =
        materialBench != null && emissions != null ? getMaterialLayerEmissionGrade(emissions, materialBench) : null;

    return {
        emissions,
        storage,
        emissionGrade: emissionGradeFromBenchmark ?? mapRowForDisplay.emissionGrade,
        storageGrade: mapRowForDisplay.storageGrade,
        defaultSource: mapRowForDisplay.source || "",
        hasMismatch: false,
        mismatchCodeLabel: undefined,
    };
};

/** User-defined framing group data on `layers` (same pattern as `insulationLayer1_embC`). */
export const FRAMING_GROUP_EMB_C_KEY = getLayerEmbCarbonFieldKey("framing");

/**
 * Display values for framing / structure embodied carbon (defaults + optional framing_embC).
 */
export const getFramingEmbodiedCarbonDisplay = ({ layers, componentType = "wall" }) => {
    const defaultFraming =
        componentType === "basementWall" ? getBasementWallFramingCarbon(layers) : getFramingCarbon(layers);

    const embC = layers?.[FRAMING_GROUP_EMB_C_KEY];
    const structureVal = layers?.structureType?.value;
    const ctsVal = layers?.componentTypeSize?.value;
    const spacingVal = layers?.spacing?.value;
    const framingVal = layers?.framing?.value;

    const hasMismatch =
        embC != null &&
        embC.emissions != null &&
        (componentType === "basementWall"
            ? String(embC.framingValue ?? "") !== String(framingVal ?? "") ||
              String(embC.spacingValue ?? "") !== String(spacingVal ?? "")
            : String(embC.structureCodeValue ?? "") !== String(structureVal ?? "") ||
              String(embC.componentTypeSizeValue ?? "") !== String(ctsVal ?? "") ||
              String(embC.spacingValue ?? "") !== String(spacingVal ?? "") ||
              String(embC.framingValue ?? "") !== String(framingVal ?? ""));

    if (embC != null && embC.emissions != null) {
        const emissions = embC.emissions;
        const storage = embC.storage ?? 0;
        return {
            emissions,
            storage,
            emissionGrade: getFramingEmissionGrade(emissions, layers, componentType),
            storageGrade: getFramingStorageGrade(storage, layers, componentType),
            defaultSource: "",
            hasMismatch,
        };
    }

    const fe = defaultFraming.emissions;
    const fs = defaultFraming.storage ?? 0;
    const emissionGrade = getFramingEmissionGrade(fe, layers, componentType);
    const storageGrade = getFramingStorageGrade(fs, layers, componentType);

    return {
        emissions: fe,
        storage: fs,
        emissionGrade,
        storageGrade,
        defaultSource: "",
        hasMismatch: false,
    };
};

// -----------------------------------------------------------------------------
// Rollup: one standard code → per-layer breakdown + totals (used in reviews / summaries)
// -----------------------------------------------------------------------------

/**
 * Calculate embodied carbon values for a standard code object.
 * @param {object} code - code object with layers
 * @returns {{ totalEmissions: number|null, totalStorage: number|null, layerBreakdown: object }}
 */
export const getEmbodiedCarbon = (code, componentType = "wall") => {
    if (!code || !code.layers) {
        return { totalEmissions: null, totalStorage: null, layerBreakdown: {} };
    }

    const { layers } = code;
    const breakdown = {};
    let totalEmissions = 0;
    let totalStorage = 0;
    let hasData = false;

    // Framing / Structure
    const defaultFramingCarbon =
        componentType === "basementWall" ? getBasementWallFramingCarbon(layers) : getFramingCarbon(layers);
    const framingEmb = layers[FRAMING_GROUP_EMB_C_KEY];

    let framingCarbon;
    if (framingEmb && framingEmb.emissions != null) {
        const fs = framingEmb.storage ?? 0;
        framingCarbon = {
            emissions: framingEmb.emissions,
            storage: fs,
            emissionGrade: getFramingEmissionGrade(framingEmb.emissions, layers, componentType),
            storageGrade: getFramingStorageGrade(fs, layers, componentType),
        };
    } else {
        const fe = defaultFramingCarbon.emissions;
        const fs = defaultFramingCarbon.storage ?? 0;
        framingCarbon = {
            ...defaultFramingCarbon,
            emissionGrade: getFramingEmissionGrade(fe, layers, componentType),
            storageGrade: getFramingStorageGrade(fs, layers, componentType),
        };
    }

    breakdown.framing = framingCarbon;

    if (framingCarbon.emissions != null) {
        totalEmissions += framingCarbon.emissions;
        totalStorage += framingCarbon.storage || 0;
        hasData = true;
    }

    const structureCode = (layers?.structureType?.value || "0").toString();
    const spacingCodeForCavity =
        componentType === "wall"
            ? getEffectiveWallSpacingCodeForCavity(layers)
            : (layers?.spacing?.value || "0").toString();
    let spacingData = FRAME_CAVITY_FRACTIONS?.[componentType]?.[structureCode]?.[spacingCodeForCavity] || {};
    if (componentType === "basementWall") {
        const framingCode = (layers?.framing?.value || "0").toString();
        const basementWallFramingType = ["0", "1", "2", "3", "4", "5", "6"].includes(framingCode) ? "wood" : "steel";
        spacingData = FRAME_CAVITY_FRACTIONS[componentType][basementWallFramingType]?.[spacingCodeForCavity] || {};
    }

    // Insulation Layer 1
    if (layers.insulationLayer1) {
        const ins1Code = (layers?.insulationLayer1?.value || "0").toString();
        const ins1Data = INSULATION_LAYER_1[ins1Code] || {};
        const cav = spacingData.cavityArea ?? 100;
        const emb1 = layers.insulationLayer1_embC;

        if (emb1 && emb1.emissions != null) {
            const emissionsAdj = (emb1.emissions * cav) / 100;
            const storageAdj = ((emb1.storage ?? 0) * cav) / 100;

            breakdown.insulationLayer1 = {
                emissions: emissionsAdj,
                storage: storageAdj,
                emissionGrade: emissionGradeFromEmbC(
                    "insulationLayer1",
                    emb1.emissions,
                    ins1Data.emissions,
                    emissionsAdj
                ),
                storageGrade: storageGradeFromEmbC(emb1.storage, ins1Data.storage, ins1Data),
            };

            totalEmissions += emissionsAdj;
            totalStorage += storageAdj;
            hasData = true;
        } else {
            breakdown.insulationLayer1 = {
                emissions: (ins1Data.emissions * cav) / 100,
                storage: (ins1Data.storage * cav) / 100,
                emissionGrade: ins1Data.emissionGrade,
                storageGrade: ins1Data.storageGrade,
            };

            if (ins1Data.emissions != null) {
                totalEmissions += (ins1Data.emissions * cav) / 100;
                totalStorage += (ins1Data.storage * cav) / 100 || 0;
                hasData = true;
            }
        }
    } else if (componentType === "basementWall" && layers.insulationFramingLayer) {
        const insFramingLayerCode = (layers?.insulationFramingLayer?.value || "0").toString();
        const insFramingLayerData = INSULATION_LAYER_1[insFramingLayerCode] || {};
        const cav = spacingData.cavityArea ?? 100;
        const embIF = layers.insulationFramingLayer_embC;

        if (embIF && embIF.emissions != null) {
            const emissionsAdj = (embIF.emissions * cav) / 100;
            const storageAdj = ((embIF.storage ?? 0) * cav) / 100;

            breakdown.insulationFramingLayer = {
                emissions: emissionsAdj,
                storage: storageAdj,
                emissionGrade: emissionGradeFromEmbC(
                    "insulationFramingLayer",
                    embIF.emissions,
                    insFramingLayerData.emissions,
                    emissionsAdj
                ),
                storageGrade: storageGradeFromEmbC(embIF.storage, insFramingLayerData.storage, insFramingLayerData),
            };

            totalEmissions += emissionsAdj;
            totalStorage += storageAdj;
            hasData = true;
        } else {
            breakdown.insulationFramingLayer = {
                emissions: (insFramingLayerData.emissions * cav) / 100,
                storage: (insFramingLayerData.storage * cav) / 100,
                emissionGrade: insFramingLayerData.emissionGrade,
                storageGrade: insFramingLayerData.storageGrade,
            };

            if (insFramingLayerData.emissions != null) {
                totalEmissions += (insFramingLayerData.emissions * cav) / 100;
                totalStorage += (insFramingLayerData.storage * cav) / 100 || 0;
                hasData = true;
            }
        }
    }

    // Insulation Layer 2
    if (layers.insulationLayer2) {
        const ins2Code = (layers?.insulationLayer2?.value || "0").toString();
        const ins2Data = INSULATION_LAYER_2[ins2Code] || {};
        const emb2 = layers.insulationLayer2_embC;

        if (emb2 && emb2.emissions != null) {
            breakdown.insulationLayer2 = {
                emissions: emb2.emissions,
                storage: emb2.storage ?? 0,
                emissionGrade: emissionGradeFromEmbC(
                    "insulationLayer2",
                    emb2.emissions,
                    ins2Data.emissions,
                    emb2.emissions
                ),
                storageGrade: storageGradeFromEmbC(emb2.storage, ins2Data.storage, ins2Data),
            };

            totalEmissions += emb2.emissions;
            totalStorage += emb2.storage ?? 0;
            hasData = true;
        } else {
            breakdown.insulationLayer2 = {
                emissions: ins2Data.emissions,
                storage: ins2Data.storage,
                emissionGrade: ins2Data.emissionGrade,
                storageGrade: ins2Data.storageGrade,
            };

            if (ins2Data.emissions != null) {
                totalEmissions += ins2Data.emissions;
                totalStorage += ins2Data.storage || 0;
                hasData = true;
            }
        }
    } else if (componentType === "basementWall" && layers.extraInsulationLayer) {
        const extraInsLayerCode = (layers?.extraInsulationLayer?.value || "0").toString();
        const extraInsLayerData = BASEMENT_WALL_EXTRA_INSULATION_LAYER[extraInsLayerCode] || {};
        const embEI = layers.extraInsulationLayer_embC;

        if (embEI && embEI.emissions != null) {
            breakdown.extraInsulationLayer = {
                emissions: embEI.emissions,
                storage: embEI.storage ?? 0,
                emissionGrade: emissionGradeFromEmbC(
                    "extraInsulationLayer",
                    embEI.emissions,
                    extraInsLayerData.emissions,
                    embEI.emissions
                ),
                storageGrade: storageGradeFromEmbC(embEI.storage, extraInsLayerData.storage, extraInsLayerData),
            };

            totalEmissions += embEI.emissions;
            totalStorage += embEI.storage ?? 0;
            hasData = true;
        } else {
            breakdown.extraInsulationLayer = {
                emissions: extraInsLayerData.emissions,
                storage: extraInsLayerData.storage,
                emissionGrade: extraInsLayerData.emissionGrade,
                storageGrade: extraInsLayerData.storageGrade,
            };

            if (extraInsLayerData.emissions != null) {
                totalEmissions += extraInsLayerData.emissions;
                totalStorage += extraInsLayerData.storage || 0;
                hasData = true;
            }
        }
    }

    // Interior
    if (layers.interior) {
        const intCode = (layers?.interior?.value || "0").toString();
        const intDataRaw = INTERIOR[intCode] || {};
        const intMaterialAlt = layers[getLayerMaterialAltFieldKey("interior")];
        const intData = resolveInteriorExteriorMaterialRow(intDataRaw, intMaterialAlt);
        const intEmb = layers.interior_embC;

        if (intEmb && intEmb.emissions != null) {
            breakdown.interior = {
                emissions: intEmb.emissions,
                storage: intEmb.storage ?? 0,
                emissionGrade: emissionGradeFromEmbC("interior", intEmb.emissions, intData.emissions, intEmb.emissions),
                storageGrade: storageGradeFromEmbC(intEmb.storage, intData.storage, intData),
            };

            totalEmissions += intEmb.emissions;
            totalStorage += intEmb.storage ?? 0;
            hasData = true;
        } else {
            breakdown.interior = {
                emissions: intData.emissions,
                storage: intData.storage,
                emissionGrade: emissionGradeFromEmbC(
                    "interior",
                    intData.emissions,
                    intData.emissions,
                    intData.emissions
                ),
                storageGrade: intData.storageGrade,
            };

            if (intData.emissions != null) {
                totalEmissions += intData.emissions;
                totalStorage += intData.storage || 0;
                hasData = true;
            }
        }
    } else if (componentType === "basementWall" && layers.interiorFinish) {
        const interiorFinishCode = (layers?.interiorFinish?.value || "0").toString();
        const interiorFinishRaw = INTERIOR[interiorFinishCode] || {};
        const ifMaterialAlt = layers[getLayerMaterialAltFieldKey("interiorFinish")];
        const interiorFinishData = resolveInteriorExteriorMaterialRow(interiorFinishRaw, ifMaterialAlt);
        const embIFin = layers.interiorFinish_embC;

        if (embIFin && embIFin.emissions != null) {
            breakdown.interiorFinish = {
                emissions: embIFin.emissions,
                storage: embIFin.storage ?? 0,
                emissionGrade: emissionGradeFromEmbC(
                    "interiorFinish",
                    embIFin.emissions,
                    interiorFinishData.emissions,
                    embIFin.emissions
                ),
                storageGrade: storageGradeFromEmbC(embIFin.storage, interiorFinishData.storage, interiorFinishData),
            };

            totalEmissions += embIFin.emissions;
            totalStorage += embIFin.storage ?? 0;
            hasData = true;
        } else {
            breakdown.interiorFinish = {
                emissions: interiorFinishData.emissions,
                storage: interiorFinishData.storage,
                emissionGrade: emissionGradeFromEmbC(
                    "interiorFinish",
                    interiorFinishData.emissions,
                    interiorFinishData.emissions,
                    interiorFinishData.emissions
                ),
                storageGrade: interiorFinishData.storageGrade,
            };

            if (interiorFinishData.emissions != null) {
                totalEmissions += interiorFinishData.emissions;
                totalStorage += interiorFinishData.storage || 0;
                hasData = true;
            }
        }
    }

    // Sheathing
    if (layers.sheathing) {
        const shCode = (layers?.sheathing?.value || "0").toString();
        const shData = SHEATHING[shCode] || {};
        const shEmb = layers.sheathing_embC;

        if (shEmb && shEmb.emissions != null) {
            breakdown.sheathing = {
                emissions: shEmb.emissions,
                storage: shEmb.storage ?? 0,
                emissionGrade: emissionGradeFromEmbC("sheathing", shEmb.emissions, shData.emissions, shEmb.emissions),
                storageGrade: storageGradeFromEmbC(shEmb.storage, shData.storage, shData),
            };

            totalEmissions += shEmb.emissions;
            totalStorage += shEmb.storage ?? 0;
            hasData = true;
        } else {
            breakdown.sheathing = {
                emissions: shData.emissions,
                storage: shData.storage,
                emissionGrade: emissionGradeFromEmbC("sheathing", shData.emissions, shData.emissions, shData.emissions),
                storageGrade: shData.storageGrade,
            };

            if (shData.emissions != null) {
                totalEmissions += shData.emissions;
                totalStorage += shData.storage || 0;
                hasData = true;
            }
        }
    }

    // Exterior
    if (layers.exterior) {
        const extCode = (layers?.exterior?.value || "0").toString();
        const extDataRaw = componentType === "floorsAbove" ? INTERIOR[extCode] || {} : EXTERIOR[extCode] || {};
        const extMaterialAlt = layers[getLayerMaterialAltFieldKey("exterior")];
        const extData = resolveInteriorExteriorMaterialRow(extDataRaw, extMaterialAlt);
        const extEmb = layers.exterior_embC;

        if (extEmb && extEmb.emissions != null) {
            breakdown.exterior = {
                emissions: extEmb.emissions,
                storage: extEmb.storage ?? 0,
                emissionGrade: emissionGradeFromEmbC("exterior", extEmb.emissions, extData.emissions, extEmb.emissions),
                storageGrade: storageGradeFromEmbC(extEmb.storage, extData.storage, extData),
            };

            totalEmissions += extEmb.emissions;
            totalStorage += extEmb.storage ?? 0;
            hasData = true;
        } else {
            breakdown.exterior = {
                emissions: extData.emissions,
                storage: extData.storage,
                emissionGrade: emissionGradeFromEmbC(
                    "exterior",
                    extData.emissions,
                    extData.emissions,
                    extData.emissions
                ),
                storageGrade: extData.storageGrade,
            };

            if (extData.emissions != null) {
                totalEmissions += extData.emissions;
                totalStorage += extData.storage || 0;
                hasData = true;
            }
        }
    }

    Object.keys(breakdown).forEach((layerKey) => {
        const layerData = breakdown[layerKey];
        if (layerData.emissions != null) {
            breakdown[layerKey].emissionPercentage =
                Math.round(10000 * ((layerData.emissions ?? 0) / totalEmissions) * 100) / 10000;
            breakdown[layerKey].storagePercentage =
                Math.round(10000 * ((layerData.storage ?? 0) / totalStorage) * 100) / 10000;
        } else {
            breakdown[layerKey].emissionPercentage = 0;
            breakdown[layerKey].storagePercentage = 0;
        }
    });

    return {
        totalEmissions: hasData ? Math.round(totalEmissions * 10000) / 10000 : null,
        totalStorage: hasData ? Math.round(totalStorage * 10000) / 10000 : null,
        layerBreakdown: breakdown,
    };
};

// -----------------------------------------------------------------------------
// UI: option list tags (dropdown labelTag enrichment)
// -----------------------------------------------------------------------------

/**
 * Enrich an array of Select options with embodied carbon grade tags.
 * Appends emission and storage grade indicators to each option's labelTag.
 * @param {Array} options - array of { label, value: { value, ... }, ... } from getOptions
 * @param {string} layerType - one of "insulationLayer1", "insulationLayer2", "interior", "sheathing", "exterior"
 * @returns {Array} options with labelTag set to grade indicators
 */
export const withEmissionGrades = (options, layerType) => {
    const dataMap = {
        insulationLayer1: INSULATION_LAYER_1,
        insulationLayer2: INSULATION_LAYER_2,
        interior: INTERIOR,
        sheathing: SHEATHING,
        exterior: EXTERIOR,
    };

    const data = dataMap[layerType];

    if (!data) return options;

    const materialBench = getMaterialEmissionsBenchmarkValue(layerType);

    return options.map((option) => {
        const code = (option.value?.value || "0").toString();
        const layerData = data[code];

        if (!layerData || (layerData.emissionGrade === "N/A" && layerData.storageGrade === "N/A")) {
            return option;
        }

        const parts = [];
        const emissionTag =
            materialBench != null && layerData.emissions != null
                ? getMaterialLayerEmissionGrade(layerData.emissions, materialBench)
                : layerData.emissionGrade && layerData.emissionGrade !== "N/A"
                  ? layerData.emissionGrade
                  : null;
        if (emissionTag && emissionTag !== "N/A") {
            parts.push(`E: ${emissionTag}`);
        }
        if (layerData.storageGrade && layerData.storageGrade !== "N/A") {
            parts.push(`S: ${layerData.storageGrade}`);
        }

        if (parts.length === 0) return option;

        const existingTag = option.labelTag || "";
        const gradeTag = parts.join(" | ");

        return {
            ...option,
            labelTag: existingTag ? `${existingTag} | ${gradeTag}` : gradeTag,
        };
    });
};

// -----------------------------------------------------------------------------
// Whole-window embodied carbon (glazing × frame lookup + optional `layers.wholeStandardWindow` EPD)
// -----------------------------------------------------------------------------

/** [maxKgPerM2, grade] — first tier where emissions ≤ cap wins; bands ~5.5 kg/m² wide from collected window data. */
const STANDARD_WINDOW_EMISSION_TIERS = [
    [72.0001, "A+"],
    [77.5, "A"],
    [83.0, "A-"],
    [88.5, "B+"],
    [94.0, "B"],
    [99.5, "B-"],
    [105.0, "C+"],
    [110.5, "C"],
    [116.0, "C-"],
];

export function getStandardWindowCodeEmissionGrade(emissions) {
    if (emissions == null || !Number.isFinite(emissions)) return "N/A";
    if (emissions === 0) return "N/A";
    for (const [maxE, grade] of STANDARD_WINDOW_EMISSION_TIERS) {
        if (emissions <= maxE) return grade;
    }
    return "D";
}

/** Coarse storage grades for whole-window (wood-frame variants carry biogenic storage). */
export function getStandardWindowCodeStorageGrade(storage) {
    if (storage == null || !Number.isFinite(storage)) return "N/A";
    if (storage >= 16) return "A";
    if (storage >= 8) return "B";
    return "N/A";
}

/** User EPD / overrides for whole-window embodied carbon, stored on `layers.wholeStandardWindow`. */
export const WINDOW_STANDARD_EMB_KEY = "wholeStandardWindow";

export function getWindowLayersSnapshotForStandardEmbC(layers) {
    if (!layers) return {};
    return {
        glazingTypeValue: layers.glazingType?.value,
        coatingsTintsValue: layers.coatingsTints?.value,
        fillTypeValue: layers.fillType?.value,
        spacerTypeValue: layers.spacerType?.value,
        windowTypeValue: layers.windowType?.value,
        frameMaterialValue: layers.frameMaterial?.value,
    };
}

export function standardWindowEmbodiedMismatch(layers, standardWindowC) {
    if (!standardWindowC || standardWindowC.emissions == null) return false;
    const cur = getWindowLayersSnapshotForStandardEmbC(layers);
    return (
        String(standardWindowC.glazingTypeValue ?? "") !== String(cur.glazingTypeValue ?? "") ||
        String(standardWindowC.coatingsTintsValue ?? "") !== String(cur.coatingsTintsValue ?? "") ||
        String(standardWindowC.fillTypeValue ?? "") !== String(cur.fillTypeValue ?? "") ||
        String(standardWindowC.spacerTypeValue ?? "") !== String(cur.spacerTypeValue ?? "") ||
        String(standardWindowC.windowTypeValue ?? "") !== String(cur.windowTypeValue ?? "") ||
        String(standardWindowC.frameMaterialValue ?? "") !== String(cur.frameMaterialValue ?? "")
    );
}

/** No map row (e.g. unsupported glazing/frame): show 0 kg/m² like other “no contribution” rows, not NaN. */
const WINDOW_UNCALCULATED = () => ({
    emissions: 0,
    storage: 0,
    emissionGrade: "N/A",
    storageGrade: "N/A",
});

export const getStandardWindowCodeEmbodiedCarbon = (layers) => {
    if (!layers) {
        return WINDOW_UNCALCULATED();
    }

    const glazingTypeValue = (layers?.glazingType?.value || "1").toString();
    const frameMaterialValue = (layers?.frameMaterial?.value || "0").toString();

    const row = lookupStandardWindowCodeEmbodiedDefaults(glazingTypeValue, frameMaterialValue);
    if (row == null || row.emissions == null) {
        return WINDOW_UNCALCULATED();
    }

    const emissionsData = {
        emissions: row.emissions,
        storage: row.storage,
        emissionGrade: "N/A",
        storageGrade: "N/A",
    };
    emissionsData.emissionGrade = getStandardWindowCodeEmissionGrade(emissionsData.emissions);
    emissionsData.storageGrade = getStandardWindowCodeStorageGrade(emissionsData.storage);
    return emissionsData;
};

/**
 * Display values for whole-window embodied carbon (map defaults + optional `layers.wholeStandardWindow` overrides).
 */
export function getStandardWindowCodeEmbodiedCarbonDisplay({ layers }) {
    const defaults = getStandardWindowCodeEmbodiedCarbon(layers);
    const standardWindowC = layers?.[WINDOW_STANDARD_EMB_KEY];
    if (standardWindowC != null && standardWindowC.emissions != null) {
        const emissions = standardWindowC.emissions;
        const storage = standardWindowC.storage ?? 0;
        return {
            emissions,
            storage,
            emissionGrade: getStandardWindowCodeEmissionGrade(emissions),
            storageGrade: getStandardWindowCodeStorageGrade(storage),
            defaultSource: "",
            hasMismatch: standardWindowEmbodiedMismatch(layers, standardWindowC),
        };
    }
    return {
        emissions: defaults.emissions,
        storage: defaults.storage,
        emissionGrade: defaults.emissionGrade,
        storageGrade: defaults.storageGrade,
        defaultSource: "",
        hasMismatch: false,
    };
}

// =============================================================================
// WIP — whole-home / assembly aggregation (user will extend; keep signatures stable)
// =============================================================================
export const roundEmbodiedKg = (value) => Math.round(value * 10000) / 10000;

export const isUdefCodeRef = (codeRef = "") => typeof codeRef === "string" && codeRef.includes("-U-");

/** User-specified R-value codes (not uDef library codes) — embodied carbon not supported. */
export const isUserSpecifiedCodeRef = (codeRef = "") => codeRef === "UserSpecified";

const getUdefAssemblyEmbodiedEmissions = (codeData = {}, componentArea = 0) => {
    if (isEmpty(codeData)) return { emissions: 0, storage: 0, layerBreakdown: {} };

    const layersArray = getUdefStandardCodeLayersArray(codeData);
    const { totalEmissions = 0, totalStorage = 0 } = getUdefEmbodiedCarbon({ layers: layersArray });
    const layerBreakdown = getUdefEmbodiedCarbonLayerBreakdown({ layers: layersArray });

    return {
        emissions: roundEmbodiedKg(totalEmissions * componentArea),
        storage: roundEmbodiedKg(totalStorage * componentArea),
        layerBreakdown,
    };
};

const getWindowEmbodiedCarbonDisplayFromCode = (codeData = {}, codeRef = "") => {
    const resolvedCodeRef = codeRef || codeData.codeRef || "";

    if (isUserSpecifiedCodeRef(resolvedCodeRef)) {
        return { display: { emissions: 0, storage: 0 }, embKey: WINDOW_STANDARD_EMB_KEY };
    }

    if (isUdefCodeRef(resolvedCodeRef)) {
        const windowLayer = getUdefWindowLayerFromCode(codeData);
        if (!windowLayer) {
            return { display: { emissions: 0, storage: 0 }, embKey: WINDOW_UDEF_EMB_KEY, udefLayerType: null };
        }
        return {
            display: getUdefWindowCodeEmbodiedCarbonDisplay({ layers: windowLayer }),
            embKey: WINDOW_UDEF_EMB_KEY,
            udefLayerType: windowLayer.type,
        };
    }

    return {
        display: getStandardWindowCodeEmbodiedCarbonDisplay({ layers: codeData.layers }),
        embKey: WINDOW_STANDARD_EMB_KEY,
    };
};

//Simple function to get the embodied emissions and storage for a component, assumes emissions and storage are per m2, componentArea is in m2
export const getAssemblyEmbodiedEmissions = (
    codeData = {},
    componentType = "wall",
    componentArea = 0,
    codeRef = ""
) => {
    if (componentArea === 0) return { emissions: 0, storage: 0, layerBreakdown: {} };

    const resolvedCodeRef = codeRef || codeData.codeRef || "";

    if (isUserSpecifiedCodeRef(resolvedCodeRef)) {
        return { emissions: 0, storage: 0, layerBreakdown: {} };
    }

    if (isUdefCodeRef(resolvedCodeRef)) {
        return getUdefAssemblyEmbodiedEmissions(codeData, componentArea);
    }

    if (isEmpty(codeData)) return { emissions: 0, storage: 0, layerBreakdown: {} };

    const { totalEmissions, totalStorage, layerBreakdown } = getEmbodiedCarbon(codeData, componentType);

    return {
        emissions: roundEmbodiedKg(totalEmissions * componentArea),
        storage: roundEmbodiedKg(totalStorage * componentArea),
        layerBreakdown,
    };
};

const getUpgradeFieldsForComponent = (upgrades = {}, componentAccessor = "") => {
    let upgradeFields = {};
    Object.values(upgrades).forEach((upgrade) => {
        const { fields = {}, selectedComponents = [] } = upgrade;
        if (selectedComponents.includes(componentAccessor)) {
            upgradeFields = {
                ...upgradeFields,
                ...fields,
            };
        }
    });
    return upgradeFields;
};

/**
 * Window opening / assembly area (m²). Dashboard templates use mm for height × width with optional `area` (m²).
 * `numIdentical` scales like fastF280 `getWindowSubCompSummary`.
 */
const getWindowAssemblyAreaM2 = (windowComp = {}) => {
    const { measurements = {}, numIdentical = 1 } = windowComp;
    const n = numIdentical > 0 ? numIdentical : 1;
    const { height, width } = measurements;

    if (height == null || width == null || !Number.isFinite(height) || !Number.isFinite(width)) return 0;
    if (height > 3 && width > 3) {
        return (height * width * n) / 1e6;
    }
    return height * width * n;
};

const getDoorOpeningAreaM2 = (doorComp = {}) => {
    const { measurements = {} } = doorComp;
    const { height, width } = measurements;
    if (height != null && width != null && Number.isFinite(height) && Number.isFinite(width)) {
        return height * width;
    }
    return 0;
};

const getFloorHeaderAssemblyAreaM2 = (floorHeaderComp = {}) => {
    const { measurements = {} } = floorHeaderComp;
    const { height, perimeter } = measurements;

    if (height != null && perimeter != null && perimeter > 0 && Number.isFinite(height) && Number.isFinite(perimeter)) {
        return height * perimeter;
    }
    return 0;
};

const getFloorHeaderAssemblyPerimeterM2 = (floorHeaderComp = {}) => {
    const { measurements = {} } = floorHeaderComp;
    const { perimeter } = measurements;

    if (perimeter != null && perimeter > 0 && Number.isFinite(perimeter)) {
        return perimeter;
    }
    return 0;
};

/**
 * Embodied carbon from wall subcomponents (windows, doors, nested door windows, floor headers) and opening areas
 * for net wall area (matches fastF280 getSubcompSpecs: subtract wall windows + full doors only from gross wall).
 */
const getSubcomponentEmbodiedEmissions = (
    parentKey,
    parentComponent = {},
    codes = {},
    upgrades = {},
    parentType = ""
) => {
    const subcomponents = parentComponent?.subcomponents || {};
    const { window: wallWindows = {}, door = {}, floorHeader = {} } = subcomponents;

    let openingWindowAreaTotal = 0;
    let openingDoorAreaTotal = 0;

    let windowEmissions = 0;
    let windowStorage = 0;
    let doorEmissions = 0;
    let doorStorage = 0;
    let floorHeaderEmissions = 0;
    let floorHeaderStorage = 0;

    const allLayerBreakdownPartial = {};
    const missingEmbodiedWarnings = [];

    const addWindowEntry = (fullKey, windowSub, { labelPrefix = "" } = {}) => {
        const { label = "", windowType: { codeRef = "", codeLabel = "" } = {} } = windowSub;
        const componentArea = getWindowAssemblyAreaM2(windowSub);

        const windowUpgradeFields = getUpgradeFieldsForComponent(upgrades, fullKey);

        const targetCodeRef = windowUpgradeFields?.windowType?.codeRef || codeRef;
        const targetCodeLabel = windowUpgradeFields?.windowType?.codeLabel || codeLabel;
        const codeData = codes?.[targetCodeRef] || {};

        const { display, embKey, udefLayerType } = getWindowEmbodiedCarbonDisplayFromCode(codeData, targetCodeRef);
        const intensityE = display.emissions ?? 0;
        const intensityS = display.storage ?? 0;
        const emissions = roundEmbodiedKg(intensityE * componentArea);
        const storage = roundEmbodiedKg(intensityS * componentArea);

        const layerBreakdownEntry = { ...display };
        if (udefLayerType) {
            layerBreakdownEntry.label = getUdefWindowLayerDisplayLabel(udefLayerType);
        }

        windowEmissions += emissions;
        windowStorage += storage;

        if (emissions === 0 && componentArea > 0) {
            missingEmbodiedWarnings.push({
                componentId: fullKey,
                componentType: "window",
                componentArea,
                label: labelPrefix ? `${labelPrefix} — ${label}` : label,
                codeRef: targetCodeRef,
                codeLabel: targetCodeLabel,
                reason: "No embodied carbon amount for this assembly (missing or zero)",
            });
        }

        allLayerBreakdownPartial[fullKey] = {
            layerBreakdown: { [embKey]: layerBreakdownEntry },
            emissions,
            storage,
            componentId: fullKey,
            componentType: "window",
            componentArea,
            label: labelPrefix ? `${labelPrefix} — ${label}` : label,
            codeRef: targetCodeRef,
            codeLabel: targetCodeLabel,
        };
    };

    Object.keys(wallWindows).forEach((winKey) => {
        const w = wallWindows[winKey];
        openingWindowAreaTotal += getWindowAssemblyAreaM2(w);
        addWindowEntry(`${parentType}.${parentKey}.subcomponents.window.${winKey}`, w);
    });

    Object.keys(door).forEach((doorKey) => {
        const d = door[doorKey];
        const fullDoorKey = `${parentType}.${parentKey}.subcomponents.door.${doorKey}`;
        const doorArea = getDoorOpeningAreaM2(d);
        openingDoorAreaTotal += doorArea;

        const { label: doorLabel = "", subcomponents: { window: windowsOnDoor = {} } = {} } = d;

        const dEmissions = 0; //Embodied emissions for doors not supported
        const dStorage = 0;

        doorEmissions += dEmissions;
        doorStorage += dStorage;

        if (dEmissions === 0 && doorArea > 0) {
            missingEmbodiedWarnings.push({
                componentId: fullDoorKey,
                componentType: "door",
                componentArea: doorArea,
                label: doorLabel,
                codeRef: "",
                codeLabel: "",
                reason: "Embodied emissions for doors not supported.",
            });
        }

        allLayerBreakdownPartial[fullDoorKey] = {
            layerBreakdown: { door: { emissions: dEmissions, storage: dStorage } },
            emissions: dEmissions,
            storage: dStorage,
            componentId: fullDoorKey,
            componentType: "door",
            componentArea: doorArea,
            label: doorLabel,
            codeRef: "",
            codeLabel: "",
        };

        Object.keys(windowsOnDoor).forEach((wodKey) => {
            const wod = windowsOnDoor[wodKey];
            addWindowEntry(
                `${parentType}.${parentKey}.subcomponents.door.${doorKey}.subcomponents.window.${wodKey}`,
                wod,
                { labelPrefix: doorLabel }
            );
        });
    });

    Object.keys(floorHeader).forEach((fhKey) => {
        const fh = floorHeader[fhKey];
        const fullFloorHeaderKey = `${parentType}.${parentKey}.subcomponents.floorHeader.${fhKey}`;
        const floorHeaderUpgradeFields = getUpgradeFieldsForComponent(
            upgrades,
            `${parentType}.${parentKey}.subcomponents.floorHeader.${fhKey}`
        );
        const { label = "", floorHeaderInsType: { codeRef = "", codeLabel = "" } = {} } = fh;

        const targetCodeRef = floorHeaderUpgradeFields?.floorHeaderInsType?.codeRef || codeRef;
        const targetCodeLabel = floorHeaderUpgradeFields?.floorHeaderInsType?.codeLabel || codeLabel;
        const componentArea = getFloorHeaderAssemblyAreaM2(fh);
        const componentPerimeter = getFloorHeaderAssemblyPerimeterM2(fh);
        const codeData = codes?.[targetCodeRef] || {};
        const {
            emissions = 0,
            storage = 0,
            layerBreakdown = {},
        } = getAssemblyEmbodiedEmissions(codeData, "floorHeader", componentArea, targetCodeRef);

        floorHeaderEmissions += emissions;
        floorHeaderStorage += storage;

        if (emissions === 0 && componentArea > 0) {
            missingEmbodiedWarnings.push({
                componentId: fullFloorHeaderKey,
                componentType: "floorHeader",
                componentArea,
                componentPerimeter,
                label,
                codeRef: targetCodeRef,
                codeLabel: targetCodeLabel,
                reason: "No embodied carbon amount for this assembly (missing or zero)",
            });
        }

        allLayerBreakdownPartial[fullFloorHeaderKey] = {
            layerBreakdown,
            emissions,
            storage,
            componentId: fullFloorHeaderKey,
            componentType: "floorHeader",
            componentArea,
            componentPerimeter,
            label,
            codeRef: targetCodeRef,
            codeLabel: targetCodeLabel,
        };
    });

    return {
        openingWindowAreaTotal,
        openingDoorAreaTotal,
        allLayerBreakdownPartial,
        missingEmbodiedWarnings,
        windowEmissions,
        windowStorage,
        doorEmissions,
        doorStorage,
        floorHeaderEmissions,
        floorHeaderStorage,
    };
};

const getFoundationDetailVolumes = ({
    componentType = "basement",
    configurationType,
    floorArea,
    perimeter, // exposedSurfacePerimeter
    height = 0, // basement walls only
    slabThickness,
    concreteWallThickness,
    footingWidth,
    footingDepth,
    footingLength,
}) => {
    let slabConcreteVolume = 0;
    let footingConcreteVolume = 0;
    let wallConcreteVolume = 0;
    if (componentType === "basement") {
        if (["BC", "BB"].includes((configurationType || "").slice(0, 2))) {
            slabConcreteVolume = floorArea * (slabThickness == null ? 0.1016 : slabThickness) || 0;
            footingConcreteVolume =
                footingWidth * footingDepth * (footingLength == null ? perimeter : footingLength) || 0;
        }

        if (["BC"].includes((configurationType || "").slice(0, 2))) {
            wallConcreteVolume =
                (concreteWallThickness == null ? 0.2032 : concreteWallThickness) * perimeter * height || 0;
        }
    } else if (["crawlspace", "slab"].includes(componentType)) {
        slabConcreteVolume = floorArea * (slabThickness == null ? 0.1016 : slabThickness) || 0;
        footingConcreteVolume = footingWidth * footingDepth * (footingLength == null ? perimeter : footingLength) || 0;
    }

    //Always treating footing as perimeter driven
    return {
        slabConcreteVolume, // ∝ area — do not scale
        wallConcreteVolume, // ∝ perimeter — scale
        footingConcreteVolume, // ∝ perimeter (or length if specified) - scale
    };
};

const getFoundationDetailEmissions = ({
    slabConcreteVolume = 0,
    wallConcreteVolume = 0,
    footingConcreteVolume = 0,
    aggregateThickness = 0,
    floorArea = 0,
    rebarLength = 0,
    includesSteelReinforcingMesh = false,
}) => {
    let perimeterDrivenEmissions = 0;
    let areaDrivenEmissions = 0;
    let fixedEmissions = 0;

    const totalConcreteVolume = footingConcreteVolume + wallConcreteVolume + slabConcreteVolume; //[m3]
    const totalConcreteEmissions = FOUNDATION_EMISSIONS.concrete.emissions * totalConcreteVolume; //[kgCO2/m3]*[m3]

    // Concrete wall and footings are perimeter driven, slab is area
    perimeterDrivenEmissions = FOUNDATION_EMISSIONS.concrete.emissions * (footingConcreteVolume + wallConcreteVolume); //[kgCO2/m3]*[m3]
    areaDrivenEmissions = FOUNDATION_EMISSIONS.concrete.emissions * slabConcreteVolume; //[kgCO2/m3]*[m3]

    //Aggregate volume and emissions
    const aggregateVolume = aggregateThickness * floorArea || 0;
    const aggregateEmissions = FOUNDATION_EMISSIONS.aggregate.emissions * aggregateVolume; //[kgCO2/m3]*[m3]

    areaDrivenEmissions += aggregateEmissions;

    //Rebar emissions
    const rebarEmissions = FOUNDATION_EMISSIONS.rebar.emissions * rebarLength || 0; //[kgCO2/m]*[m]
    fixedEmissions += rebarEmissions;

    //Steel mesh emissions
    const steelMeshEmissions =
        FOUNDATION_EMISSIONS.steelMesh.emissions * (includesSteelReinforcingMesh ? floorArea : 0) || 0; //[kgCO2/m2]*[m2]
    areaDrivenEmissions += steelMeshEmissions;

    return {
        //Used for core calculations
        totalConcreteEmissions,
        aggregateEmissions,
        rebarEmissions,
        steelMeshEmissions,
        //Used for form scaling calculations
        perimeterDrivenEmissions,
        areaDrivenEmissions,
        fixedEmissions,
    };
};

/**
 * Whole-home embodied carbon totals from enclosure components and material codes.
 */
export const getOverallEmbodiedEmissions = (components, codes, upgradePackageId = "base", upgradePackages = {}) => {
    let totalEmissions = 0;
    let totalStorage = 0;
    const allLayerBreakdown = {};
    const missingEmbodiedWarnings = [];

    const { wall = {}, ceiling = {}, expFloor = {}, basement = {}, crawlspace = {}, slab = {} } = components || {};

    //Check for upgrades to be merged over components later
    const upgrades = upgradePackages[upgradePackageId]?.upgrades || {};

    let totalWallEmissions = 0;
    let totalWallStorage = 0;
    let totalCeilingEmissions = 0;
    let totalCeilingStorage = 0;
    let totalExpFloorEmissions = 0;
    let totalExpFloorStorage = 0;
    let totalBasementEmissions = 0;
    let totalBasementStorage = 0;
    let totalCrawlspaceEmissions = 0;
    let totalSlabEmissions = 0;
    let totalSlabStorage = 0;

    let totalWindowEmissions = 0;
    let totalWindowStorage = 0;

    let totalDoorEmissions = 0;
    let totalDoorStorage = 0;

    let totalFloorHeaderEmissions = 0;
    let totalFloorHeaderStorage = 0;

    Object.keys(wall).forEach((wallKey) => {
        const wallComp = wall[wallKey] || {};
        const wallUpgradeFields = getUpgradeFieldsForComponent(upgrades, `wall.${wallKey}`);

        const {
            label,
            wallInsType: { codeRef = "", codeLabel = "" } = {},
            measurements: { height = 0, perimeter = 0 } = {},
        } = wallComp;

        const targetCodeRef = wallUpgradeFields?.wallInsType?.codeRef || codeRef;
        const targetCodeLabel = wallUpgradeFields?.wallInsType?.codeLabel || codeLabel;

        const grossWallArea = height * perimeter;
        const subComponentDetails = getSubcomponentEmbodiedEmissions(wallKey, wallComp, codes, upgrades, "wall");
        const {
            openingWindowAreaTotal,
            openingDoorAreaTotal,
            allLayerBreakdownPartial,
            missingEmbodiedWarnings: subWarnings,
            windowEmissions,
            windowStorage,
            doorEmissions,
            doorStorage,
            floorHeaderEmissions,
            floorHeaderStorage,
        } = subComponentDetails;

        const netWallArea = Math.max(grossWallArea - (openingWindowAreaTotal + openingDoorAreaTotal), 0);
        const codeData = codes?.[targetCodeRef] || {};

        const {
            emissions = 0,
            storage = 0,
            layerBreakdown = {},
        } = getAssemblyEmbodiedEmissions(codeData, "wall", netWallArea, targetCodeRef);

        totalEmissions += emissions;
        totalStorage += storage;

        totalWallEmissions += emissions;
        totalWallStorage += storage;

        totalEmissions += windowEmissions + doorEmissions + floorHeaderEmissions;
        totalStorage += windowStorage + doorStorage + floorHeaderStorage;

        totalWindowEmissions += windowEmissions;
        totalWindowStorage += windowStorage;
        totalDoorEmissions += doorEmissions;
        totalDoorStorage += doorStorage;
        totalFloorHeaderEmissions += floorHeaderEmissions;
        totalFloorHeaderStorage += floorHeaderStorage;

        if (emissions === 0 && netWallArea > 0) {
            missingEmbodiedWarnings.push({
                componentId: wallKey,
                componentType: "wall",
                componentArea: netWallArea,
                label,
                codeRef: targetCodeRef,
                codeLabel: targetCodeLabel,
                reason: "No embodied carbon amount for this assembly (missing or zero)",
            });
        }

        missingEmbodiedWarnings.push(...subWarnings);

        allLayerBreakdown[wallKey] = {
            layerBreakdown,
            emissions,
            storage,
            componentId: wallKey,
            componentType: "wall",
            componentArea: netWallArea,
            componentPerimeter: perimeter,
            label,
            codeRef: targetCodeRef,
            codeLabel: targetCodeLabel,
        };

        Object.assign(allLayerBreakdown, allLayerBreakdownPartial);
    });

    Object.keys(ceiling).forEach((ceilingKey) => {
        const ceilingComp = ceiling[ceilingKey] || {};
        const ceilingUpgradeFields = getUpgradeFieldsForComponent(upgrades, `ceiling.${ceilingKey}`);

        const {
            label,
            ceilingInsType: { codeRef = "", codeLabel = "" } = {},
            measurements: { area } = {},
            constructionType: { id: constructTypeId } = {},
        } = ceilingComp;

        const targetCodeRef = ceilingUpgradeFields?.ceilingInsType?.codeRef || codeRef;
        const targetCodeLabel = ceilingUpgradeFields?.ceilingInsType?.codeLabel || codeLabel;

        const isFlat = [2, 3].includes(constructTypeId);
        const grossCeilingArea = area; // m²

        const subComponentDetails = getSubcomponentEmbodiedEmissions(
            ceilingKey,
            ceilingComp,
            codes,
            upgrades,
            "ceiling"
        );
        const {
            openingWindowAreaTotal,
            allLayerBreakdownPartial,
            missingEmbodiedWarnings: subWarnings,
            windowEmissions,
            windowStorage,
        } = subComponentDetails;

        const netCeilingArea = Math.max(grossCeilingArea - openingWindowAreaTotal, 0);

        const codeData = codes?.[targetCodeRef] || {};

        const {
            emissions = 0,
            storage = 0,
            layerBreakdown = {},
        } = getAssemblyEmbodiedEmissions(codeData, isFlat ? "ceilingFlat" : "ceiling", netCeilingArea, targetCodeRef);

        totalEmissions += emissions;
        totalStorage += storage;

        totalCeilingEmissions += emissions;
        totalCeilingStorage += storage;

        totalEmissions += windowEmissions;
        totalStorage += windowStorage;

        totalWindowEmissions += windowEmissions;
        totalWindowStorage += windowStorage;

        if (emissions === 0) {
            missingEmbodiedWarnings.push({
                componentId: ceilingKey,
                componentType: isFlat ? "ceilingFlat" : "ceiling",
                componentArea: netCeilingArea,
                label,
                codeRef: targetCodeRef,
                codeLabel: targetCodeLabel,
                reason: "No embodied carbon amount for this assembly (missing or zero)",
            });
        }

        missingEmbodiedWarnings.push(...subWarnings);

        allLayerBreakdown[ceilingKey] = {
            layerBreakdown,
            emissions,
            storage,
            componentId: ceilingKey,
            componentType: isFlat ? "ceilingFlat" : "ceiling",
            componentArea: netCeilingArea,
            label,
            codeRef: targetCodeRef,
            codeLabel: targetCodeLabel,
        };

        Object.assign(allLayerBreakdown, allLayerBreakdownPartial);
    });

    Object.keys(expFloor).forEach((expFloorKey) => {
        const expFloorUpgradeFields = getUpgradeFieldsForComponent(upgrades, `expFloor.${expFloorKey}`);

        const {
            label,
            expFloorInsType: { codeRef = "", codeLabel = "" } = {},
            measurements: { area } = {},
        } = expFloor[expFloorKey] || {};

        const targetCodeRef = expFloorUpgradeFields?.expFloorInsType?.codeRef || codeRef;
        const targetCodeLabel = expFloorUpgradeFields?.expFloorInsType?.codeLabel || codeLabel;

        const componentArea = area; // m²
        const codeData = codes?.[targetCodeRef] || {};

        const {
            emissions = 0,
            storage = 0,
            layerBreakdown = {},
        } = getAssemblyEmbodiedEmissions(codeData, "expFloor", componentArea, targetCodeRef);

        totalEmissions += emissions;
        totalStorage += storage;

        totalExpFloorEmissions += emissions;
        totalExpFloorStorage += storage;

        if (emissions === 0) {
            missingEmbodiedWarnings.push({
                componentId: expFloorKey,
                componentType: "expFloor",
                componentArea,
                label,
                codeRef: targetCodeRef,
                codeLabel: targetCodeLabel,
                reason: "No embodied carbon amount for this assembly (missing or zero)",
            });
        }

        allLayerBreakdown[expFloorKey] = {
            layerBreakdown,
            emissions,
            storage,
            componentId: expFloorKey,
            componentType: "expFloor",
            componentArea,
            label,
            codeRef: targetCodeRef,
            codeLabel: targetCodeLabel,
        };
    });

    Object.keys(basement).forEach((basementKey) => {
        const basementComp = basement[basementKey] || {};
        const basementUpgradeFields = getUpgradeFieldsForComponent(upgrades, `basement.${basementKey}`);
        const {
            label,
            configuration: { type: configurationType = "" } = {},
            exposedSurfacePerimeter,
            wall: {
                measurements: { height } = {},
                intAddedInsType: { codeRef: intAddedInsCodeRef = "", codeLabel: intAddedInsCodeLabel = "" } = {},
                extAddedInsType: { id: extAddedInsId, value: extAddedInsValue = 0 } = {},
                ponyWall: {
                    enabled: ponyWallEnabled = false,
                    ponyWallHeight = 0,
                    ponyWallInsType: {
                        codeRef: ponyWallInsTypeCodeRef = "",
                        codeLabel: ponyWallInsTypeCodeLabel = "",
                    } = {},
                } = {},
            } = {},
            floor: {
                measurements: { isRectangular, area, perimeter, length, width } = {},
                floorsAboveInsType: { codeRef: floorsAboveCodeRef = "", codeLabel: floorsAboveCodeLabel = "" } = {},
                slabInsType: { codeRef: slabInsCodeRef = "", codeLabel: slabInsCodeLabel = "", id: slabInsId } = {},
            } = {},
            detailedMeasurements: {
                footingWidth,
                footingDepth,
                footingLength,
                aggregateThickness,
                rebarLength,
                concreteWallThickness,
                slabThickness,
                includesSteelReinforcingMesh,
            } = {},
        } = basementComp;

        const targetIntAddedInsCodeRef = basementUpgradeFields?.wall?.intAddedInsCodeRef?.codeRef || intAddedInsCodeRef;
        const targetIntAddedInsCodeLabel =
            basementUpgradeFields?.wall?.intAddedInsCodeRef?.codeLabel || intAddedInsCodeLabel;

        const targetPonyWallCodeRef =
            basementUpgradeFields?.wall?.ponyWall?.ponyWallInsType?.codeRef || ponyWallInsTypeCodeRef;
        const targetPonyWallCodeLabel =
            basementUpgradeFields?.wall?.ponyWall?.ponyWallInsType?.codeLabel || ponyWallInsTypeCodeLabel;
        const targetFloorsAboveCodeRef =
            basementUpgradeFields?.floor?.floorsAboveInsType?.codeRef || floorsAboveCodeRef;
        const targetFloorsAboveCodeLabel =
            basementUpgradeFields?.floor?.floorsAboveInsType?.codeLabel || floorsAboveCodeLabel;
        const targetSlabInsId = basementUpgradeFields?.floor?.slabInsType?.id || slabInsId;

        const subComponentDetails = getSubcomponentEmbodiedEmissions(
            basementKey,
            basementComp,
            codes,
            upgrades,
            "basement"
        );
        const {
            openingWindowAreaTotal,
            openingDoorAreaTotal,
            allLayerBreakdownPartial,
            missingEmbodiedWarnings: subWarnings,
            windowEmissions,
            windowStorage,
            doorEmissions,
            doorStorage,
            floorHeaderEmissions,
            floorHeaderStorage,
        } = subComponentDetails;

        const floorArea = isRectangular ? length * width : area; // m²

        let netExposedWallArea = exposedSurfacePerimeter * height; // m²

        let subComponentArea = openingWindowAreaTotal + openingDoorAreaTotal;

        //Pony wall (subtract area if applicable from exposed wall area)
        if (ponyWallEnabled && ponyWallHeight > 0) {
            const ponyWallArea = ponyWallHeight * exposedSurfacePerimeter;
            netExposedWallArea -= ponyWallArea;

            const netPonyWallArea = ponyWallArea - subComponentArea;
            const ponyWallCodeData = codes?.[targetPonyWallCodeRef] || {};
            const {
                emissions: ponyWallEmissions = 0,
                storage: ponyWallStorage = 0,
                layerBreakdown: ponyWallLayerBreakdown = {},
            } = getAssemblyEmbodiedEmissions(
                ponyWallCodeData,
                "crawlspaceWall",
                netPonyWallArea,
                targetPonyWallCodeRef
            );

            totalEmissions += ponyWallEmissions;
            totalStorage += ponyWallStorage;
            totalBasementEmissions += ponyWallEmissions;
            totalBasementStorage += ponyWallStorage;
            if (ponyWallEmissions === 0) {
                missingEmbodiedWarnings.push({
                    componentId: basementKey + "-PonyWall",
                    componentType: "basementPonyWall",
                    componentArea: netPonyWallArea,
                    label: label + " - Pony Wall Insulation",
                    codeRef: ponyWallInsTypeCodeRef,
                    codeLabel: ponyWallInsTypeCodeLabel,
                    reason: "No embodied carbon amount for this assembly (missing or zero)",
                });
            }
            allLayerBreakdown[basementKey + "-PonyWall"] = {
                layerBreakdown: ponyWallLayerBreakdown,
                emissions: ponyWallEmissions,
                storage: ponyWallStorage,
                componentId: basementKey + "-PonyWall",
                componentType: "basementPonyWall",
                componentArea: netPonyWallArea,
                componentPerimeter: exposedSurfacePerimeter,
                label: label + " - Pony Wall Insulation",
                codeRef: ponyWallInsTypeCodeRef,
                codeLabel: ponyWallInsTypeCodeLabel,
            };
        } else {
            //Subtract subcomponent area from exposed wall area
            netExposedWallArea -= subComponentArea;
        }

        //Basement exterior wall insulation warning
        if (extAddedInsValue > 0) {
            missingEmbodiedWarnings.push({
                componentId: basementKey + "-ExteriorWallInsulation",
                componentType: "basementWall",
                componentArea: netExposedWallArea,
                label: label + " - Exterior Wall Insulation",
                reason: "No embodied carbon amount for this assembly (missing or zero)",
            });
        }

        //Basement Interior Wall Insulation
        const intAddedInsCodeData = codes?.[targetIntAddedInsCodeRef] || {};
        const {
            emissions: wallEmissions = 0,
            storage: wallStorage = 0,
            layerBreakdown: wallLayerBreakdown = {},
        } = getAssemblyEmbodiedEmissions(
            intAddedInsCodeData,
            "basementWall",
            netExposedWallArea,
            targetIntAddedInsCodeRef
        );

        totalEmissions += wallEmissions;
        totalStorage += wallStorage;
        totalBasementEmissions += wallEmissions;
        totalBasementStorage += wallStorage;
        if (wallEmissions === 0) {
            missingEmbodiedWarnings.push({
                componentId: basementKey,
                componentType: "basementWall",
                componentArea: netExposedWallArea,
                componentPerimeter: exposedSurfacePerimeter,
                label: label + " - Interior Wall Insulation",
                codeRef: targetIntAddedInsCodeRef,
                codeLabel: targetIntAddedInsCodeLabel,
                reason: "No embodied carbon amount for this assembly (missing or zero)",
            });
        }

        allLayerBreakdown[basementKey + "-Wall"] = {
            layerBreakdown: wallLayerBreakdown,
            emissions: wallEmissions,
            storage: wallStorage,
            componentId: basementKey + "-Wall",
            componentType: "basementWall",
            componentArea: netExposedWallArea,
            componentPerimeter: exposedSurfacePerimeter,
            label: label + " - Interior Wall Insulation",
            codeRef: targetIntAddedInsCodeRef,
            codeLabel: targetIntAddedInsCodeLabel,
        };

        //Basement Floors Above
        const floorsAboveCodeData = codes?.[targetFloorsAboveCodeRef] || {};
        const {
            emissions: floorsAboveEmissions = 0,
            storage: floorsAboveStorage = 0,
            layerBreakdown: floorsAboveLayerBreakdown = {},
        } = getAssemblyEmbodiedEmissions(floorsAboveCodeData, "floorsAbove", floorArea, targetFloorsAboveCodeRef);

        totalEmissions += floorsAboveEmissions;
        totalStorage += floorsAboveStorage;
        totalBasementEmissions += floorsAboveEmissions;
        totalBasementStorage += floorsAboveStorage;
        if (floorsAboveEmissions === 0) {
            missingEmbodiedWarnings.push({
                componentId: basementKey + "-FloorsAbove",
                componentType: "floorsAbove",
                componentArea: floorArea,
                label: label + " - Floors Above",
                codeRef: targetFloorsAboveCodeRef,
                codeLabel: targetFloorsAboveCodeLabel,
                reason: "No embodied carbon amount for this assembly (missing or zero)",
            });
        }

        allLayerBreakdown[basementKey + "-FloorsAbove"] = {
            layerBreakdown: floorsAboveLayerBreakdown,
            emissions: floorsAboveEmissions,
            storage: floorsAboveStorage,
            componentId: basementKey + "-FloorsAbove",
            componentType: "floorsAbove",
            componentArea: floorArea,
            label: label + " - Floors Above",
            codeRef: targetFloorsAboveCodeRef,
            codeLabel: targetFloorsAboveCodeLabel,
        };

        //Basement Slab Insulation
        //Codes in slab insulation not supported (because they aren't used in ERS)
        if (Object.keys(FOUNDATION_SLAB_INSULATION_EMISSIONS).includes(targetSlabInsId?.toString() || "")) {
            const { emissions: slabInsEmissions = 0, storage: slabInsStorage = 0 } =
                FOUNDATION_SLAB_INSULATION_EMISSIONS[targetSlabInsId];
            totalEmissions += slabInsEmissions * floorArea;
            totalStorage += slabInsStorage * floorArea;
            totalBasementEmissions += slabInsEmissions * floorArea;
            totalBasementStorage += slabInsStorage * floorArea;

            if (slabInsEmissions === 0) {
                missingEmbodiedWarnings.push({
                    componentId: basementKey + "-Slab",
                    componentType: "basementSlab",
                    componentArea: floorArea,
                    label: label + " - Slab Insulation",
                    codeRef: "",
                    codeLabel: "",
                    reason: "No embodied carbon amount for this assembly (missing or zero)",
                });
            }
            allLayerBreakdown[basementKey + "-Slab"] = {
                layerBreakdown: {
                    slabIns: { emissions: slabInsEmissions * floorArea, storage: slabInsStorage * floorArea },
                },
                emissions: slabInsEmissions * floorArea,
                storage: slabInsStorage * floorArea,
                componentId: basementKey + "-Slab",
                componentType: "basementSlab",
                componentArea: floorArea,
                label: label + " - Slab Insulation",
                codeRef: "",
                codeLabel: "",
            };
        } else {
            missingEmbodiedWarnings.push({
                componentId: basementKey + "-Slab",
                componentType: "basementSlab",
                componentArea: floorArea,
                label: label + " - Slab Insulation",
                codeRef: "",
                codeLabel: "",
                reason: "No embodied carbon amount for this assembly (missing or zero)",
            });
        }

        //Add subcomponent emissions to total emissions and storage
        totalEmissions += windowEmissions + doorEmissions + floorHeaderEmissions;
        totalStorage += windowStorage + doorStorage + floorHeaderStorage;

        totalWindowEmissions += windowEmissions;
        totalWindowStorage += windowStorage;
        totalDoorEmissions += doorEmissions;
        totalDoorStorage += doorStorage;
        totalFloorHeaderEmissions += floorHeaderEmissions;
        totalFloorHeaderStorage += floorHeaderStorage;

        // ====================== Calculate foundation concrete volume ======================
        //"BC" = Concrete Basement, "BW" = Wood Basement, "BB" = Concrete and Wood Basement

        const { slabConcreteVolume, wallConcreteVolume, footingConcreteVolume } = getFoundationDetailVolumes({
            componentType: "basement",
            configurationType,
            floorArea,
            perimeter: exposedSurfacePerimeter, // exposedSurfacePerimeter
            height, // basement walls only
            slabThickness,
            concreteWallThickness,
            footingWidth,
            footingDepth,
            footingLength,
        });

        //Handle missing values tracking for slab and footing concrete volumes.
        if (["BC", "BB"].includes(configurationType.slice(0, 2))) {
            if (slabConcreteVolume === 0) {
                missingEmbodiedWarnings.push({
                    componentId: basementKey + "-SlabConcrete",
                    componentType: "basement",
                    componentArea: floorArea,
                    label: label + " - Slab Concrete",
                    reason: "Slab thickness is missing or zero",
                });
            }

            if (footingConcreteVolume === 0) {
                missingEmbodiedWarnings.push({
                    componentId: basementKey + "-FootingConcrete",
                    componentType: "basement",
                    componentArea: 0,
                    label: label + " - Footing Concrete",
                    reason: "Footing measurements are missing or zero",
                });
            }
        } else {
            missingEmbodiedWarnings.push({
                componentId: basementKey + "-WoodSlab",
                componentType: "basement",
                componentArea: 0,
                label: label + " - Wood Slab",
                reason: "Wood slab basements are not supported",
            });
        }

        //Handle missing values tracking for wall concrete volume.
        if (["BC"].includes(configurationType.slice(0, 2))) {
            if (wallConcreteVolume === 0) {
                missingEmbodiedWarnings.push({
                    componentId: basementKey + "-WallConcrete",
                    componentType: "basement",
                    componentArea: 0,
                    label: label + " - Wall Concrete",
                    reason: "Wall thickness is missing or zero",
                });
            }
        }

        const {
            totalConcreteEmissions = 0,
            aggregateEmissions = 0,
            rebarEmissions = 0,
            steelMeshEmissions = 0,
            perimeterDrivenEmissions = 0,
            areaDrivenEmissions = 0,
            fixedEmissions = 0,
        } = getFoundationDetailEmissions({
            slabConcreteVolume,
            wallConcreteVolume,
            footingConcreteVolume,
            aggregateThickness,
            floorArea,
            rebarLength,
            includesSteelReinforcingMesh,
        });

        //Concrete emissions
        totalEmissions += totalConcreteEmissions;
        totalBasementEmissions += totalConcreteEmissions;

        //Calculate aggregate volume and emissions
        totalEmissions += aggregateEmissions;
        totalBasementEmissions += aggregateEmissions;

        //Calculate rebar emissions
        totalEmissions += rebarEmissions;
        totalBasementEmissions += rebarEmissions;

        //Calculate steel mesh emissions
        totalEmissions += steelMeshEmissions;
        totalBasementEmissions += steelMeshEmissions;

        allLayerBreakdown[basementKey + "-AdditionalDetails"] = {
            layerBreakdown: {
                concrete: { emissions: totalConcreteEmissions, storage: 0 },
                aggregate: { emissions: aggregateEmissions, storage: 0 },
                rebar: { emissions: rebarEmissions, storage: 0 },
                steelMesh: { emissions: steelMeshEmissions, storage: 0 },
            },
            scalingBreakdown: {
                perimeterDrivenEmissions,
                areaDrivenEmissions,
                fixedEmissions,
            },
            emissions: totalConcreteEmissions + aggregateEmissions + rebarEmissions + steelMeshEmissions,
            storage: 0,
            componentId: basementKey + "-AdditionalDetails",
            componentType: "basement",
            label: label + " - Additional Details",
            codeRef: "",
            codeLabel: "",
        };

        missingEmbodiedWarnings.push(...subWarnings);

        Object.assign(allLayerBreakdown, allLayerBreakdownPartial);
    });

    Object.keys(crawlspace).forEach((crawlspaceKey) => {
        const crawlspaceComp = crawlspace[crawlspaceKey] || {};
        const crawlspaceUpgradeFields = getUpgradeFieldsForComponent(upgrades, `crawlspace.${crawlspaceKey}`);
        const {
            label,
            exposedSurfacePerimeter,
            wall: {
                measurements: { height } = {},
                crawlspaceWallInsType: {
                    codeRef: crawlspaceWallCodeRef = "",
                    codeLabel: crawlspaceWallCodeLabel = "",
                } = {},
            } = {},
            floor: {
                measurements: { isRectangular, area, perimeter, length, width } = {},
                floorsAboveInsType: { codeRef: floorsAboveCodeRef = "", codeLabel: floorsAboveCodeLabel = "" } = {},
                slabInsType: { codeRef: slabInsCodeRef = "", codeLabel: slabInsCodeLabel = "", id: slabInsId } = {},
            } = {},
            detailedMeasurements: {
                footingWidth,
                footingDepth,
                footingLength,
                aggregateThickness,
                rebarLength,
                // concreteWallThickness,
                slabThickness,
                includesSteelReinforcingMesh,
            } = {},
        } = crawlspaceComp;

        const targetCrawlspaceWallCodeRef =
            crawlspaceUpgradeFields?.wall?.crawlspaceWallInsType?.codeRef || crawlspaceWallCodeRef;
        const targetCrawlspaceWallCodeLabel =
            crawlspaceUpgradeFields?.wall?.crawlspaceWallInsType?.codeLabel || crawlspaceWallCodeLabel;
        const targetFloorsAboveCodeRef =
            crawlspaceUpgradeFields?.floor?.floorsAboveInsType?.codeRef || floorsAboveCodeRef;
        const targetFloorsAboveCodeLabel =
            crawlspaceUpgradeFields?.floor?.floorsAboveInsType?.codeLabel || floorsAboveCodeLabel;
        const targetSlabInsId = crawlspaceUpgradeFields?.floor?.slabInsType?.id || slabInsId;

        const subComponentDetails = getSubcomponentEmbodiedEmissions(
            crawlspaceKey,
            crawlspaceComp,
            codes,
            upgrades,
            "crawlspace"
        );
        const {
            openingWindowAreaTotal,
            openingDoorAreaTotal,
            allLayerBreakdownPartial,
            missingEmbodiedWarnings: subWarnings,
            windowEmissions,
            windowStorage,
            doorEmissions,
            doorStorage,
            floorHeaderEmissions,
            floorHeaderStorage,
        } = subComponentDetails;

        const floorArea = isRectangular ? length * width : area; // m²
        const floorPerimeter = isRectangular ? 2 * (length + width) : perimeter; // m

        let netExposedWallArea = exposedSurfacePerimeter * height; // m²
        let subComponentArea = openingWindowAreaTotal + openingDoorAreaTotal;
        //Subtract subcomponent area from exposed wall area
        netExposedWallArea -= subComponentArea;

        //Crawlspace Wall Insulation
        const crawlspaceWallCodeData = codes?.[targetCrawlspaceWallCodeRef] || {};
        const {
            emissions: wallEmissions = 0,
            storage: wallStorage = 0,
            layerBreakdown: wallLayerBreakdown = {},
        } = getAssemblyEmbodiedEmissions(
            crawlspaceWallCodeData,
            "crawlspaceWall",
            netExposedWallArea,
            targetCrawlspaceWallCodeRef
        );

        totalEmissions += wallEmissions;
        totalStorage += wallStorage;
        totalBasementEmissions += wallEmissions;
        totalBasementStorage += wallStorage;
        if (wallEmissions === 0) {
            missingEmbodiedWarnings.push({
                componentId: crawlspaceKey,
                componentType: "crawlspaceWall",
                componentArea: netExposedWallArea,
                label: label + " - Wall Insulation",
                codeRef: targetCrawlspaceWallCodeRef,
                codeLabel: targetCrawlspaceWallCodeLabel,
                reason: "No embodied carbon amount for this assembly (missing or zero)",
            });
        }

        allLayerBreakdown[crawlspaceKey + "-Wall"] = {
            layerBreakdown: wallLayerBreakdown,
            emissions: wallEmissions,
            storage: wallStorage,
            componentId: crawlspaceKey + "-Wall",
            componentType: "crawlspaceWall",
            componentArea: netExposedWallArea,
            label: label + " - Wall Insulation",
            codeRef: targetCrawlspaceWallCodeRef,
            codeLabel: targetCrawlspaceWallCodeLabel,
        };

        //Crawlspace Floors Above
        const floorsAboveCodeData = codes?.[targetFloorsAboveCodeRef] || {};
        const {
            emissions: floorsAboveEmissions = 0,
            storage: floorsAboveStorage = 0,
            layerBreakdown: floorsAboveLayerBreakdown = {},
        } = getAssemblyEmbodiedEmissions(floorsAboveCodeData, "floorsAbove", floorArea, targetFloorsAboveCodeRef);

        totalEmissions += floorsAboveEmissions;
        totalStorage += floorsAboveStorage;
        totalBasementEmissions += floorsAboveEmissions;
        totalBasementStorage += floorsAboveStorage;
        if (floorsAboveEmissions === 0) {
            missingEmbodiedWarnings.push({
                componentId: crawlspaceKey + "-FloorsAbove",
                componentType: "floorsAbove",
                componentArea: floorArea,
                label: label + " - Floors Above",
                codeRef: targetFloorsAboveCodeRef,
                codeLabel: targetFloorsAboveCodeLabel,
                reason: "No embodied carbon amount for this assembly (missing or zero)",
            });
        }

        allLayerBreakdown[crawlspaceKey + "-FloorsAbove"] = {
            layerBreakdown: floorsAboveLayerBreakdown,
            emissions: floorsAboveEmissions,
            storage: floorsAboveStorage,
            componentId: crawlspaceKey + "-FloorsAbove",
            componentType: "floorsAbove",
            componentArea: floorArea,
            label: label + " - Floors Above",
            codeRef: targetFloorsAboveCodeRef,
            codeLabel: targetFloorsAboveCodeLabel,
        };

        //Crawlspace Slab Insulation
        //Codes in slab insulation not supported (because they aren't used in ERS)
        if (Object.keys(FOUNDATION_SLAB_INSULATION_EMISSIONS).includes(targetSlabInsId?.toString() || "")) {
            const { emissions: slabInsEmissions = 0, storage: slabInsStorage = 0 } =
                FOUNDATION_SLAB_INSULATION_EMISSIONS[targetSlabInsId];
            totalEmissions += slabInsEmissions * floorArea;
            totalStorage += slabInsStorage * floorArea;
            totalBasementEmissions += slabInsEmissions * floorArea;
            totalBasementStorage += slabInsStorage * floorArea;

            if (slabInsEmissions === 0) {
                missingEmbodiedWarnings.push({
                    componentId: crawlspaceKey + "-Slab",
                    componentType: "crawlspaceSlab",
                    componentArea: floorArea,
                    label: label + " - Slab Insulation",
                    codeRef: "",
                    codeLabel: "",
                    reason: "No embodied carbon amount for this assembly (missing or zero)",
                });
            }
            allLayerBreakdown[crawlspaceKey + "-Slab"] = {
                layerBreakdown: {
                    slabIns: { emissions: slabInsEmissions * floorArea, storage: slabInsStorage * floorArea },
                },
                emissions: slabInsEmissions * floorArea,
                storage: slabInsStorage * floorArea,
                componentId: crawlspaceKey + "-Slab",
                componentType: "crawlspaceSlab",
                componentArea: floorArea,
                label: label + " - Slab Insulation",
                codeRef: "",
                codeLabel: "",
            };
        } else {
            missingEmbodiedWarnings.push({
                componentId: crawlspaceKey + "-Slab",
                componentType: "crawlspaceSlab",
                componentArea: floorArea,
                label: label + " - Slab Insulation",
                codeRef: "",
                codeLabel: "",
                reason: "No embodied carbon amount for this assembly (missing or zero)",
            });
        }

        //Add subcomponent emissions to total emissions and storage
        totalEmissions += windowEmissions + doorEmissions + floorHeaderEmissions;
        totalStorage += windowStorage + doorStorage + floorHeaderStorage;

        totalWindowEmissions += windowEmissions;
        totalWindowStorage += windowStorage;
        totalDoorEmissions += doorEmissions;
        totalDoorStorage += doorStorage;
        totalFloorHeaderEmissions += floorHeaderEmissions;
        totalFloorHeaderStorage += floorHeaderStorage;

        // ====================== Calculate foundation concrete volume ======================
        //Calculate slab and footing concrete volume

        const { slabConcreteVolume, footingConcreteVolume } = getFoundationDetailVolumes({
            componentType: "crawlspace",
            configurationType: "",
            floorArea,
            perimeter: exposedSurfacePerimeter, // exposedSurfacePerimeter
            height: 0, // basement walls only
            slabThickness,
            concreteWallThickness: 0,
            footingWidth,
            footingDepth,
            footingLength,
        });

        if (slabConcreteVolume === 0) {
            missingEmbodiedWarnings.push({
                componentId: crawlspaceKey + "-SlabConcrete",
                componentType: "crawlspace",
                componentArea: floorArea,
                label: label + " - Slab Concrete",
                reason: "Slab thickness is missing or zero",
            });
        }

        if (footingConcreteVolume === 0) {
            missingEmbodiedWarnings.push({
                componentId: crawlspaceKey + "-FootingConcrete",
                componentType: "crawlspace",
                componentArea: 0,
                label: label + " - Footing Concrete",
                reason: "Footing measurements are missing or zero",
            });
        }

        const {
            totalConcreteEmissions = 0,
            aggregateEmissions = 0,
            rebarEmissions = 0,
            steelMeshEmissions = 0,
            perimeterDrivenEmissions = 0,
            areaDrivenEmissions = 0,
            fixedEmissions = 0,
        } = getFoundationDetailEmissions({
            slabConcreteVolume,
            wallConcreteVolume: 0,
            footingConcreteVolume,
            aggregateThickness,
            floorArea,
            rebarLength,
            includesSteelReinforcingMesh,
        });

        //Concrete emissions
        totalEmissions += totalConcreteEmissions;
        totalBasementEmissions += totalConcreteEmissions;

        //Calculate aggregate volume and emissions
        totalEmissions += aggregateEmissions;
        totalBasementEmissions += aggregateEmissions;

        //Calculate rebar emissions
        totalEmissions += rebarEmissions;
        totalBasementEmissions += rebarEmissions;

        //Calculate steel mesh emissions
        totalEmissions += steelMeshEmissions;
        totalBasementEmissions += steelMeshEmissions;

        allLayerBreakdown[crawlspaceKey + "-AdditionalDetails"] = {
            layerBreakdown: {
                concrete: { emissions: totalConcreteEmissions, storage: 0 },
                aggregate: { emissions: aggregateEmissions, storage: 0 },
                rebar: { emissions: rebarEmissions, storage: 0 },
                steelMesh: { emissions: steelMeshEmissions, storage: 0 },
            },
            scalingBreakdown: {
                perimeterDrivenEmissions,
                areaDrivenEmissions,
                fixedEmissions,
            },
            emissions: totalConcreteEmissions + aggregateEmissions + rebarEmissions + steelMeshEmissions,
            storage: 0,
            componentId: crawlspaceKey + "-AdditionalDetails",
            componentType: "crawlspace",
            label: label + " - Additional Details",
            codeRef: "",
            codeLabel: "",
        };

        missingEmbodiedWarnings.push(...subWarnings);

        Object.assign(allLayerBreakdown, allLayerBreakdownPartial);
    });

    Object.keys(slab).forEach((slabKey) => {
        const slabComp = slab[slabKey] || {};
        const slabUpgradeFields = getUpgradeFieldsForComponent(upgrades, `slab.${slabKey}`);
        const {
            label,
            exposedSurfacePerimeter,
            floor: {
                measurements: { isRectangular, area, perimeter, length, width } = {},
                slabInsType: { codeRef: slabInsCodeRef = "", codeLabel: slabInsCodeLabel = "", id: slabInsId } = {},
            } = {},
            detailedMeasurements: {
                footingWidth,
                footingDepth,
                footingLength,
                aggregateThickness,
                rebarLength,
                // concreteWallThickness,
                slabThickness,
                includesSteelReinforcingMesh,
            } = {},
        } = slabComp;

        const targetSlabInsId = slabUpgradeFields?.floor?.slabInsType?.id || slabInsId;

        const floorArea = isRectangular ? length * width : area; // m²

        //Slab Insulation
        //Codes in slab insulation not supported (because they aren't used in ERS)
        if (Object.keys(FOUNDATION_SLAB_INSULATION_EMISSIONS).includes(targetSlabInsId?.toString() || "")) {
            const { emissions: slabInsEmissions = 0, storage: slabInsStorage = 0 } =
                FOUNDATION_SLAB_INSULATION_EMISSIONS[targetSlabInsId];
            totalEmissions += slabInsEmissions * floorArea;
            totalStorage += slabInsStorage * floorArea;
            totalBasementEmissions += slabInsEmissions * floorArea;
            totalBasementStorage += slabInsStorage * floorArea;

            if (slabInsEmissions === 0) {
                missingEmbodiedWarnings.push({
                    componentId: slabKey + "-Slab",
                    componentType: "slab",
                    componentArea: floorArea,
                    label: label + " - Slab Insulation",
                    codeRef: "",
                    codeLabel: "",
                    reason: "No embodied carbon amount for this assembly (missing or zero)",
                });
            }
            allLayerBreakdown[slabKey + "-Slab"] = {
                layerBreakdown: {
                    slabIns: { emissions: slabInsEmissions * floorArea, storage: slabInsStorage * floorArea },
                },
                emissions: slabInsEmissions * floorArea,
                storage: slabInsStorage * floorArea,
                componentId: slabKey + "-Slab",
                componentType: "slab",
                componentArea: floorArea,
                label: label + " - Slab Insulation",
                codeRef: "",
                codeLabel: "",
            };
        } else {
            missingEmbodiedWarnings.push({
                componentId: slabKey + "-Slab",
                componentType: "slab",
                componentArea: floorArea,
                label: label + " - Slab Insulation",
                codeRef: "",
                codeLabel: "",
                reason: "No embodied carbon amount for this assembly (missing or zero)",
            });
        }

        // ====================== Calculate foundation concrete volume ======================
        //Calculate slab and footing concrete volume
        const { slabConcreteVolume, footingConcreteVolume } = getFoundationDetailVolumes({
            componentType: "slab",
            configurationType: "",
            floorArea,
            perimeter: exposedSurfacePerimeter, // exposedSurfacePerimeter
            height: 0, // basement walls only
            slabThickness,
            concreteWallThickness: 0,
            footingWidth,
            footingDepth,
            footingLength,
        });

        if (slabConcreteVolume === 0) {
            missingEmbodiedWarnings.push({
                componentId: slabKey + "-SlabConcrete",
                componentType: "slab",
                componentArea: floorArea,
                label: label + " - Slab Concrete",
                reason: "Slab thickness is missing or zero",
            });
        }

        if (footingConcreteVolume === 0) {
            missingEmbodiedWarnings.push({
                componentId: slabKey + "-FootingConcrete",
                componentType: "slab",
                componentArea: 0,
                label: label + " - Footing Concrete",
                reason: "Footing measurements are missing or zero",
            });
        }

        const {
            totalConcreteEmissions = 0,
            aggregateEmissions = 0,
            rebarEmissions = 0,
            steelMeshEmissions = 0,
            perimeterDrivenEmissions = 0,
            areaDrivenEmissions = 0,
            fixedEmissions = 0,
        } = getFoundationDetailEmissions({
            slabConcreteVolume,
            wallConcreteVolume: 0,
            footingConcreteVolume,
            aggregateThickness,
            floorArea,
            rebarLength,
            includesSteelReinforcingMesh,
        });

        //Concrete emissions
        totalEmissions += totalConcreteEmissions;
        totalBasementEmissions += totalConcreteEmissions;

        //Calculate aggregate volume and emissions
        totalEmissions += aggregateEmissions;
        totalBasementEmissions += aggregateEmissions;

        //Calculate rebar emissions
        totalEmissions += rebarEmissions;
        totalBasementEmissions += rebarEmissions;

        //Calculate steel mesh emissions
        totalEmissions += steelMeshEmissions;
        totalBasementEmissions += steelMeshEmissions;

        allLayerBreakdown[slabKey + "-AdditionalDetails"] = {
            layerBreakdown: {
                concrete: { emissions: totalConcreteEmissions, storage: 0 },
                aggregate: { emissions: aggregateEmissions, storage: 0 },
                rebar: { emissions: rebarEmissions, storage: 0 },
                steelMesh: { emissions: steelMeshEmissions, storage: 0 },
            },
            scalingBreakdown: {
                perimeterDrivenEmissions,
                areaDrivenEmissions,
                fixedEmissions,
            },
            componentPerimeter: exposedSurfacePerimeter, //Needed for form-factor scaling because we don't have walls to track
            emissions: totalConcreteEmissions + aggregateEmissions + rebarEmissions + steelMeshEmissions,
            storage: 0,
            componentId: slabKey + "-AdditionalDetails",
            componentType: "slab",
            label: label + " - Additional Details",
            codeRef: "",
            codeLabel: "",
        };
    });

    return {
        packageId: upgradePackageId,
        label: upgradePackages[upgradePackageId]?.name || upgradePackageId,
        totalEmbodiedCarbonKg: totalEmissions,
        totalCarbonStorageKg: totalStorage,
        allLayerBreakdown,
        byComponentType: {
            wall: { emissions: totalWallEmissions, storage: totalWallStorage },
            ceiling: { emissions: totalCeilingEmissions, storage: totalCeilingStorage },
            expFloor: { emissions: totalExpFloorEmissions, storage: totalExpFloorStorage },
            basement: { emissions: totalBasementEmissions, storage: totalBasementStorage },
            crawlspace: { emissions: totalCrawlspaceEmissions, storage: totalCrawlspaceEmissions },
            slab: { emissions: totalSlabEmissions, storage: totalSlabStorage },
            window: { emissions: totalWindowEmissions, storage: totalWindowStorage },
            door: { emissions: totalDoorEmissions, storage: totalDoorStorage },
            floorHeader: { emissions: totalFloorHeaderEmissions, storage: totalFloorHeaderStorage },
        },
        missingEmbodiedWarnings,
    };
};

const FORM_FACTOR_SHAPES = ["circle", "square", "rectangle", "l-shape", "h-shape"];

const calculateEquivalentPerimeter = (shape, floorArea) => {
    if (!Number.isFinite(floorArea) || floorArea <= 0) {
        return 0;
    }

    if (shape === "circle") {
        return 2 * Math.PI * Math.sqrt(floorArea / Math.PI);
    } else if (shape === "square") {
        return 4 * Math.sqrt(floorArea);
    } else if (shape === "rectangle") {
        const WtoL = 0.5; //Width to length ratio (x = w/l = 0.5)
        return 2 * (1 + WtoL) * Math.sqrt(floorArea / WtoL);
    } else if (shape === "l-shape") {
        // const WtoL = 0.5; //Width to length ratio (x = w/l = 0.5) FIXED
        return 4 * Math.sqrt((4 * floorArea) / 3);
    } else if (shape === "h-shape") {
        // const WtoL = 1/3 //Width to length ratio (x = w/l = 1/3) FIXED
        return (16 / 3) * Math.sqrt((9 * floorArea) / 7);
    } else {
        return 0;
    }
};

//Storey ID to tell us how many storeys there are in the model so we can divide the above grade floor area by it
const storeyDivisor = (storeysId) => {
    const selectedStorey = {
        0: { storeys: 1, segments: [1] },
        1: { storeys: 1.5, segments: [2, 1] },
        2: { storeys: 2, segments: [1, 1] },
        3: { storeys: 2.5, segments: [2, 2, 1] },
        4: { storeys: 3, segments: [1, 1, 1] },
        5: { storeys: 2, segments: [1, 1] },
        6: { storeys: 2, segments: [1, 1] },
    }?.[storeysId] || { storeys: 1, segments: [1] };

    return {
        storeys: selectedStorey.storeys,
        segments: selectedStorey.segments,
        sumSegments: selectedStorey.segments.reduce((acc, curr) => acc + curr, 0),
    };
};

const safeScale = (value, newPerimeter, totalPerimeter) => {
    const v = Number(value);
    const p = Number(newPerimeter);
    const t = Number(totalPerimeter);
    if (!Number.isFinite(v) || !Number.isFinite(p) || !Number.isFinite(t) || t <= 0) {
        return Number.isFinite(v) ? v : 0; // keep original when we can't scale
    }
    return (v * p) / t;
};

//Takes the embodied carbon result from getOverallEmbodiedEmissions and returns the form-factor values for the package
export const getEmbodiedFormFactors = (embodiedCarbonResult, components = {}, dimensions = {}, numStoreysId = 0) => {
    const { allLayerBreakdown = {} } = embodiedCarbonResult;

    //Get the model data, mainly dimensions
    const {
        aboveGradeIntFloorArea: { total: aboveGradeIntFloorAreaTotal = 0 } = {},
        belowGradeIntFloorArea: { total: belowGradeIntFloorAreaTotal = 0 } = {},
        volume: { total: volumeTotal = 0 } = {},
    } = dimensions || {};

    const { basement = {}, crawlspace = {}, slab = {}, wall = {}, ceiling = {}, expFloor = {} } = components || {};

    //Loop through allLayerBreakdown only once and grab what we need.
    //Ceilings and exposed floors don't get scaled because we're only changing perimeters, not (projected) floor areas.
    let totalBasementWallPerimeter = 0;
    const basementWalls = {};
    let totalBasementFloorHeaderPerimeter = 0; //Not explicitly needed because we scale based on the parent walls
    const basementFloorHeaders = {};
    let totalCrawlspaceWallPerimeter = 0;
    const crawlspaceWalls = {};
    let totalCrawlspaceFloorHeaderPerimeter = 0; //Not explicitly needed because we scale based on the parent wall
    const crawlspaceFloorHeaders = {};
    let totalWallPerimeter = 0;
    const walls = {};
    let totalWallFloorHeaderPerimeter = 0; //Not explicitly needed because we scale based on the parent walls
    const wallFloorHeaders = {};
    let totalBasementPonyWallPerimeter = 0; //Not explicitly needed because we scale based on the parent basement walls
    const basementPonyWalls = {};

    const basementAdditionalDetails = {};
    const crawlspaceAdditionalDetails = {};
    const slabAdditionalDetails = {};
    let totalSlabPerimeter = 0;

    const wallWindows = {};
    const basementWindows = {};
    const crawlspaceWindows = {};
    Object.keys(allLayerBreakdown).forEach((compKey) => {
        const { componentType, componentPerimeter, emissions, storage, componentId, scalingBreakdown } =
            allLayerBreakdown[compKey];
        if (componentType === "basementWall") {
            totalBasementWallPerimeter += Number(componentPerimeter) || 0;
            basementWalls[compKey] = { perimeter: componentPerimeter, emissions, storage };
        }

        if (componentType === "floorHeader" && (componentId || "").startsWith("basement.")) {
            totalBasementFloorHeaderPerimeter += Number(componentPerimeter) || 0;
            basementFloorHeaders[compKey] = { perimeter: componentPerimeter, emissions, storage };
        }

        if (componentType === "crawlspaceWall") {
            totalCrawlspaceWallPerimeter += Number(componentPerimeter) || 0;
            crawlspaceWalls[compKey] = { perimeter: componentPerimeter, emissions, storage };
        }

        if (componentType === "floorHeader" && (componentId || "").startsWith("crawlspace.")) {
            totalCrawlspaceFloorHeaderPerimeter += Number(componentPerimeter) || 0;
            crawlspaceFloorHeaders[compKey] = { perimeter: componentPerimeter, emissions, storage };
        }

        if (componentType === "wall") {
            totalWallPerimeter += Number(componentPerimeter) || 0;
            walls[compKey] = { perimeter: componentPerimeter, emissions, storage };
        }

        if (componentType === "floorHeader" && (componentId || "").startsWith("wall.")) {
            totalWallFloorHeaderPerimeter += Number(componentPerimeter) || 0;
            wallFloorHeaders[compKey] = { perimeter: componentPerimeter, emissions, storage };
        }

        if (componentType === "basementPonyWall") {
            totalBasementPonyWallPerimeter += Number(componentPerimeter) || 0;
            basementPonyWalls[compKey] = { perimeter: componentPerimeter, emissions, storage };
        }

        if (componentType === "basement" && (componentId || "").endsWith("AdditionalDetails")) {
            basementAdditionalDetails[compKey] = { emissions, storage, scalingBreakdown };
        }

        if (componentType === "crawlspace" && (componentId || "").endsWith("AdditionalDetails")) {
            crawlspaceAdditionalDetails[compKey] = { emissions, storage, scalingBreakdown };
        }

        if (componentType === "slab" && (componentId || "").endsWith("AdditionalDetails")) {
            slabAdditionalDetails[compKey] = { emissions, storage, scalingBreakdown };
            totalSlabPerimeter += Number(componentPerimeter) || 0;
        }

        if (componentType === "window" && (componentId || "").startsWith("wall.") && !componentId.includes(".door.")) {
            wallWindows[compKey] = { emissions, storage };
        }
        if (
            componentType === "window" &&
            (componentId || "").startsWith("basement.") &&
            !componentId.includes(".door.")
        ) {
            basementWindows[compKey] = { emissions, storage };
        }
        if (
            componentType === "window" &&
            (componentId || "").startsWith("crawlspace.") &&
            !componentId.includes(".door.")
        ) {
            crawlspaceWindows[compKey] = { emissions, storage };
        }
    });

    // ======================================================== BASEMENT ======================================================== //
    //Get current total basement floor areas, More direct than the dimensions fields
    let totalBsmntFloorArea = 0;
    Object.keys(basement).forEach((basementKey) => {
        const { floor: { measurements: { isRectangular, area = 0, length = 0, width = 0 } = {} } = {} } =
            basement[basementKey];
        totalBsmntFloorArea += isRectangular ? length * width : area;
    });

    //Calculate the total basement wall perimeter we'd have for each shape
    const basementWallEqPerimeters = {};
    FORM_FACTOR_SHAPES.forEach((shape) => {
        const equivalentPerimeter = calculateEquivalentPerimeter(shape, totalBsmntFloorArea);
        basementWallEqPerimeters[shape] = equivalentPerimeter;
    });

    Object.keys(basementWalls).forEach((compKey) => {
        const { emissions, storage } = basementWalls[compKey];

        FORM_FACTOR_SHAPES.forEach((shape) => {
            const newFormPerimeter = basementWallEqPerimeters[shape];

            const newEmissions = safeScale(emissions, newFormPerimeter, totalBasementWallPerimeter);
            const newStorage = safeScale(storage, newFormPerimeter, totalBasementWallPerimeter);

            basementWalls[compKey].formFactor = {
                ...basementWalls[compKey].formFactor,
                [shape]: {
                    emissions: newEmissions,
                    storage: newStorage,
                },
            };
        });
    });

    //Basement floor headers get same scaling as basement walls
    Object.keys(basementFloorHeaders).forEach((compKey) => {
        const { emissions, storage } = basementFloorHeaders[compKey];

        FORM_FACTOR_SHAPES.forEach((shape) => {
            const newFormPerimeter = basementWallEqPerimeters[shape];

            const newEmissions = safeScale(emissions, newFormPerimeter, totalBasementWallPerimeter);
            const newStorage = safeScale(storage, newFormPerimeter, totalBasementWallPerimeter);

            basementFloorHeaders[compKey].formFactor = {
                ...basementFloorHeaders[compKey].formFactor,
                [shape]: { emissions: newEmissions, storage: newStorage },
            };
        });
    });

    //Basement pony walls get same scaling as basement walls
    Object.keys(basementPonyWalls).forEach((compKey) => {
        const { emissions, storage } = basementPonyWalls[compKey];

        FORM_FACTOR_SHAPES.forEach((shape) => {
            const newFormPerimeter = basementWallEqPerimeters[shape];

            const newEmissions = safeScale(emissions, newFormPerimeter, totalBasementWallPerimeter);
            const newStorage = safeScale(storage, newFormPerimeter, totalBasementWallPerimeter);

            basementPonyWalls[compKey].formFactor = {
                ...basementPonyWalls[compKey].formFactor,
                [shape]: { emissions: newEmissions, storage: newStorage },
            };
        });
    });

    Object.keys(basementAdditionalDetails).forEach((compKey) => {
        const { scalingBreakdown } = basementAdditionalDetails[compKey];
        const { perimeterDrivenEmissions = 0, areaDrivenEmissions = 0, fixedEmissions = 0 } = scalingBreakdown || {};
        FORM_FACTOR_SHAPES.forEach((shape) => {
            const newFormPerimeter = basementWallEqPerimeters[shape];

            const newEmissions =
                areaDrivenEmissions +
                fixedEmissions +
                safeScale(perimeterDrivenEmissions, newFormPerimeter, totalBasementWallPerimeter);
            const newStorage = 0; //Always 0 for additional details

            basementAdditionalDetails[compKey].formFactor = {
                ...basementAdditionalDetails[compKey].formFactor,
                [shape]: { emissions: newEmissions, storage: newStorage },
            };
        });
    });

    Object.keys(basementWindows).forEach((compKey) => {
        const { emissions, storage } = basementWindows[compKey];

        FORM_FACTOR_SHAPES.forEach((shape) => {
            const newFormPerimeter = basementWallEqPerimeters[shape];

            const newEmissions = safeScale(emissions, newFormPerimeter, totalBasementWallPerimeter);
            const newStorage = safeScale(storage, newFormPerimeter, totalBasementWallPerimeter);

            basementWindows[compKey].formFactor = {
                ...basementWindows[compKey].formFactor,
                [shape]: { emissions: newEmissions, storage: newStorage },
            };
        });
    });

    // ================================================================ CRAWL SPACE ======================================================== //
    //Get current total crawl space floor areas, can't trust the values from the dimensions fields
    let totalCrawlspaceFloorArea = 0;
    Object.keys(crawlspace).forEach((crawlspaceKey) => {
        const { floor: { measurements: { isRectangular, area = 0, length = 0, width = 0 } = {} } = {} } =
            crawlspace[crawlspaceKey];
        totalCrawlspaceFloorArea += isRectangular ? length * width : area;
    });

    //Calculate the total crawl space wall perimeter we'd have for each shape
    const crawlspaceWallEqPerimeters = {};
    FORM_FACTOR_SHAPES.forEach((shape) => {
        const equivalentPerimeter = calculateEquivalentPerimeter(shape, totalCrawlspaceFloorArea);
        crawlspaceWallEqPerimeters[shape] = equivalentPerimeter;
    });

    Object.keys(crawlspaceWalls).forEach((compKey) => {
        const { emissions, storage } = crawlspaceWalls[compKey];

        FORM_FACTOR_SHAPES.forEach((shape) => {
            const newFormPerimeter = crawlspaceWallEqPerimeters[shape];

            const newEmissions = safeScale(emissions, newFormPerimeter, totalCrawlspaceWallPerimeter);
            const newStorage = safeScale(storage, newFormPerimeter, totalCrawlspaceWallPerimeter);

            crawlspaceWalls[compKey].formFactor = {
                ...crawlspaceWalls[compKey].formFactor,
                [shape]: {
                    emissions: newEmissions,
                    storage: newStorage,
                },
            };
        });
    });

    //Crawlspace floor headers get same scaling as crawlspace walls
    Object.keys(crawlspaceFloorHeaders).forEach((compKey) => {
        const { emissions, storage } = crawlspaceFloorHeaders[compKey];

        FORM_FACTOR_SHAPES.forEach((shape) => {
            const newFormPerimeter = crawlspaceWallEqPerimeters[shape];

            const newEmissions = safeScale(emissions, newFormPerimeter, totalCrawlspaceWallPerimeter);
            const newStorage = safeScale(storage, newFormPerimeter, totalCrawlspaceWallPerimeter);

            crawlspaceFloorHeaders[compKey].formFactor = {
                ...crawlspaceFloorHeaders[compKey].formFactor,
                [shape]: { emissions: newEmissions, storage: newStorage },
            };
        });
    });

    Object.keys(crawlspaceAdditionalDetails).forEach((compKey) => {
        const { scalingBreakdown } = crawlspaceAdditionalDetails[compKey];
        const { perimeterDrivenEmissions = 0, areaDrivenEmissions = 0, fixedEmissions = 0 } = scalingBreakdown || {};
        FORM_FACTOR_SHAPES.forEach((shape) => {
            const newFormPerimeter = crawlspaceWallEqPerimeters[shape];

            const newEmissions =
                areaDrivenEmissions +
                fixedEmissions +
                safeScale(perimeterDrivenEmissions, newFormPerimeter, totalCrawlspaceWallPerimeter);
            const newStorage = 0; //Always 0 for additional details

            crawlspaceAdditionalDetails[compKey].formFactor = {
                ...crawlspaceAdditionalDetails[compKey].formFactor,
                [shape]: { emissions: newEmissions, storage: newStorage },
            };
        });
    });

    Object.keys(crawlspaceWindows).forEach((compKey) => {
        const { emissions, storage } = crawlspaceWindows[compKey];

        FORM_FACTOR_SHAPES.forEach((shape) => {
            const newFormPerimeter = crawlspaceWallEqPerimeters[shape];

            const newEmissions = safeScale(emissions, newFormPerimeter, totalCrawlspaceWallPerimeter);
            const newStorage = safeScale(storage, newFormPerimeter, totalCrawlspaceWallPerimeter);

            crawlspaceWindows[compKey].formFactor = {
                ...crawlspaceWindows[compKey].formFactor,
                [shape]: { emissions: newEmissions, storage: newStorage },
            };
        });
    });

    // ================================================================ SLAB ======================================================== //
    //Slabs don't have walls
    let totalSlabFloorArea = 0;
    Object.keys(slab).forEach((slabKey) => {
        const { floor: { measurements: { isRectangular, area = 0, length = 0, width = 0 } = {} } = {} } = slab[slabKey];
        totalSlabFloorArea += isRectangular ? length * width : area;
    });

    const slabEqPerimeters = {};
    FORM_FACTOR_SHAPES.forEach((shape) => {
        const equivalentPerimeter = calculateEquivalentPerimeter(shape, totalSlabFloorArea);
        slabEqPerimeters[shape] = equivalentPerimeter;
    });

    Object.keys(slabAdditionalDetails).forEach((compKey) => {
        const { scalingBreakdown } = slabAdditionalDetails[compKey];
        const { perimeterDrivenEmissions = 0, areaDrivenEmissions = 0, fixedEmissions = 0 } = scalingBreakdown || {};
        FORM_FACTOR_SHAPES.forEach((shape) => {
            const newFormPerimeter = slabEqPerimeters[shape];

            const newEmissions =
                areaDrivenEmissions +
                fixedEmissions +
                safeScale(perimeterDrivenEmissions, newFormPerimeter, totalSlabPerimeter);
            const newStorage = 0; //Always 0 for additional details

            slabAdditionalDetails[compKey].formFactor = {
                ...slabAdditionalDetails[compKey].formFactor,
                [shape]: { emissions: newEmissions, storage: newStorage },
            };
        });
    });

    // ================================================================ WALL ======================================================== //
    //Get the floor area of each storey, including partial storeys.
    //e.g. 200m2 for a 2 storey building is 100m2 per storey, this would mean 2 circles with 100m2 area each.
    // for half storeys (250m2 @ 2.5 storeys) we'd have 2 circles with 100m2 area each and 1 circle with 50m2 area.
    const numberOfStoreys = storeyDivisor(numStoreysId);
    const { segments, sumSegments } = numberOfStoreys;

    //Calculate the total wall perimeter we'd have for each shape
    const wallEqPerimeters = {};
    FORM_FACTOR_SHAPES.forEach((shape) => {
        let equivalentPerimeter = 0;

        //Loop through each segment, this handles one circle for one storey, two circles for two storeys, etc.
        segments.forEach((segment) => {
            equivalentPerimeter += calculateEquivalentPerimeter(
                shape,
                aboveGradeIntFloorAreaTotal * (segment / sumSegments)
            );
        });

        wallEqPerimeters[shape] = equivalentPerimeter;
    });

    Object.keys(walls).forEach((compKey) => {
        const { emissions, storage } = walls[compKey];

        FORM_FACTOR_SHAPES.forEach((shape) => {
            const newFormPerimeter = wallEqPerimeters[shape];

            const newEmissions = safeScale(emissions, newFormPerimeter, totalWallPerimeter);
            const newStorage = safeScale(storage, newFormPerimeter, totalWallPerimeter);

            walls[compKey].formFactor = {
                ...walls[compKey].formFactor,
                [shape]: {
                    emissions: newEmissions,
                    storage: newStorage,
                },
            };
        });
    });

    //Wall floor headers get same scaling as walls
    Object.keys(wallFloorHeaders).forEach((compKey) => {
        const { emissions, storage } = wallFloorHeaders[compKey];

        FORM_FACTOR_SHAPES.forEach((shape) => {
            const newFormPerimeter = wallEqPerimeters[shape];

            const newEmissions = safeScale(emissions, newFormPerimeter, totalWallPerimeter);
            const newStorage = safeScale(storage, newFormPerimeter, totalWallPerimeter);

            wallFloorHeaders[compKey].formFactor = {
                ...wallFloorHeaders[compKey].formFactor,
                [shape]: { emissions: newEmissions, storage: newStorage },
            };
        });
    });

    Object.keys(wallWindows).forEach((compKey) => {
        const { emissions, storage } = wallWindows[compKey];

        FORM_FACTOR_SHAPES.forEach((shape) => {
            const newFormPerimeter = wallEqPerimeters[shape];

            const newEmissions = safeScale(emissions, newFormPerimeter, totalWallPerimeter);
            const newStorage = safeScale(storage, newFormPerimeter, totalWallPerimeter);

            wallWindows[compKey].formFactor = {
                ...wallWindows[compKey].formFactor,
                [shape]: { emissions: newEmissions, storage: newStorage },
            };
        });
    });

    // =================================================== COMPILE NEW VALUES ============================================== //

    // Returning the new values
    const allNewValues = {
        ...basementWalls,
        ...crawlspaceWalls,
        ...basementFloorHeaders,
        ...crawlspaceFloorHeaders,
        ...walls,
        ...wallFloorHeaders,
        ...basementPonyWalls,
        ...basementAdditionalDetails,
        ...crawlspaceAdditionalDetails,
        ...slabAdditionalDetails,
        ...wallWindows,
        ...basementWindows,
        ...crawlspaceWindows,
    };

    const shapeEmissionResult = FORM_FACTOR_SHAPES.reduce((acc, shape) => {
        let newEmissions = 0;
        let newStorage = 0;
        Object.keys(allLayerBreakdown).forEach((compKey) => {
            if (allNewValues[compKey]?.formFactor?.[shape]) {
                const { emissions, storage } = allNewValues[compKey].formFactor[shape];
                newEmissions += emissions;
                newStorage += storage;
            } else {
                const { emissions, storage } = allLayerBreakdown[compKey];
                newEmissions += emissions;
                newStorage += storage;
            }
        });

        acc[shape] = {
            emissions: newEmissions,
            storage: newStorage,
        };
        return acc;
    }, {});

    return shapeEmissionResult;
};
